"use client"

import React, { useState, useRef, useCallback, useEffect } from "react";
import { FaPencilAlt, FaMousePointer, FaMinus, FaVectorSquare, FaRegCircle, FaPlay, FaTrash, FaServer, FaDatabase, FaCloud, FaNetworkWired, FaShieldAlt, FaCog, FaRocket, FaLayerGroup, FaExchangeAlt, FaSearch, FaFileAlt, FaKey, FaUsers, FaGlobe, FaMobile, FaDesktop, FaCogs, FaChartLine, FaBell, FaEnvelope, FaCalendar, FaMapMarkerAlt, FaCreditCard, FaShoppingCart, FaHeart, FaStar, FaThumbsUp, FaComment, FaShare, FaBookmark, FaDownload, FaUpload, FaSync, FaPause, FaStop, FaForward, FaBackward, FaRandom, FaVolumeUp, FaVolumeMute, FaEye, FaEyeSlash, FaLock, FaUnlock, FaUserSecret, FaUserCheck, FaUserTimes, FaUserPlus, FaUserMinus, FaUserEdit, FaUserCog, FaUserShield, FaUserGraduate, FaUserTie, FaUserNinja, FaUserAstronaut, FaUserDoctor, FaUserNurse, FaUserInjured, FaUserFriends, FaUserAlt, FaUserCircle, FaUserClock, FaUserTag, FaUserSlash, FaUserLock, FaUserUnlock, FaCode, FaPlus, FaClock, FaArrowsAltH, FaUndo, FaRedo } from 'react-icons/fa';
import { postRequest, getRequest } from '../../Fetch/request';
import { useLocation } from 'react-router-dom';
import Loader from '../Loader/Loader';
import { Snackbar, Alert } from '@mui/material';
import './HLD.css';



export default function HldDraw({ problemId: propProblemId, problem: propProblem, documentVisible = false, setDocumentVisible = () => {}, onUnsavedChangesChange = () => {} }) {
  const [shapes, setShapes] = useState([]);
  const [connections, setConnections] = useState([]);
  const [selectedTool, setSelectedTool] = useState("select");
  const [draggedShape, setDraggedShape] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [connectionStart, setConnectionStart] = useState(null);
  const [problem, setProblem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [hasLoadedFromLocalStorage, setHasLoadedFromLocalStorage] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [copiedShapes, setCopiedShapes] = useState([]);
  const [copiedConnections, setCopiedConnections] = useState([]);
  const initialSelectedPositionsRef = useRef(new Map());
  const svgRef = useRef(null);
  const svgContainerRef = useRef(null);

  // ── Undo / Redo ──────────────────────────────────────────────────────────
  const historyRef = useRef([]);          // array of { shapes, connections } snapshots
  const historyIndexRef = useRef(-1);     // index of current snapshot
  const shapesRef = useRef([]);           // always-current shapes (updated via effect)
  const connectionsRef = useRef([]);      // always-current connections
  const [historyVersion, setHistoryVersion] = useState(0); // bump to re-render buttons

  // Get problemId from props or URL
  const loc = useLocation();
  const problemId = propProblemId || new URLSearchParams(loc.search).get('problemId');



  // Reset initialization flags when problemId changes
  useEffect(() => {
    if (problemId) {
      setHasInitialized(false);
      setHasLoadedFromLocalStorage(false);
      // Clear history when switching problems
      historyRef.current = [];
      historyIndexRef.current = -1;
      setHistoryVersion(0);
    }
  }, [problemId]);

  // Keep refs in sync so saveHistory can read latest state without stale closures
  useEffect(() => { shapesRef.current = shapes; }, [shapes]);
  useEffect(() => { connectionsRef.current = connections; }, [connections]);

  // Sync problem prop to state when it changes (important when navigating between steps)
  useEffect(() => {
    if (propProblem) {
      setProblem(propProblem);
    } else if (problemId) {
      // If no prop, try to load from localStorage
      const problemKey = `hld-problem-${problemId}`;
      const problemData = localStorage.getItem(problemKey);
      if (problemData) {
        try {
          const parsed = JSON.parse(problemData);
          setProblem(parsed);
        } catch (e) {
          console.error('Failed to parse HLD problem data from localStorage:', e);
          // Fallback: Set basic problem object
          setProblem({
            problemId: problemId,
            title: 'HLD Problem',
            description: 'High-Level Design Problem'
          });
        }
      } else {
        // Fallback: Set basic problem object to prevent "Problem not found"
        setProblem({
          problemId: problemId,
          title: 'HLD Problem',
          description: 'High-Level Design Problem'
        });
      }
    }
  }, [propProblem, problemId]);

  // Save drawing to localStorage on every action for persistence across steps
  useEffect(() => {
    if (!problemId || !hasLoadedFromLocalStorage) return; // Don't save until initial load is complete

    const attemptKey = `hld-attempt-${problemId}`;
    const drawing = {
      shapes,
      connections
    };

    try {
      // Get existing attempt data or create new
      const existingData = localStorage.getItem(attemptKey);
      let attemptData = {};

      if (existingData) {
        try {
          attemptData = JSON.parse(existingData);
        } catch (e) {
          console.error('Failed to parse existing attempt data:', e);
        }
      }

      // Update drawing in attempt data
      attemptData.drawing = drawing;
      if (!attemptData.steps) {
        attemptData.steps = {};
      }
      attemptData.steps.hldDiagram = { drawing };
      attemptData.timestamp = Date.now();

      // Save to localStorage immediately
      localStorage.setItem(attemptKey, JSON.stringify(attemptData));

      // Capture SVG snapshot and update localStorage + dispatch event
      if (shapes.length > 0 && svgRef.current) {
        captureSnapshotAsync(svgRef.current, shapes, (snapshot) => {
          if (!snapshot) return;
          try {
            const current = JSON.parse(localStorage.getItem(attemptKey) || '{}');
            current.diagramSnapshot = snapshot;
            localStorage.setItem(attemptKey, JSON.stringify(current));
          } catch (e) {
            // LocalStorage quota exceeded — save without snapshot
            console.warn('Could not save diagram snapshot:', e);
          }
          window.dispatchEvent(new CustomEvent('hldDiagramSnapshotUpdated', {
            detail: { problemId, snapshot }
          }));
        });
      }
    } catch (error) {
      console.error('Failed to save drawing to localStorage:', error);
    }
  }, [shapes, connections, problemId, hasLoadedFromLocalStorage]);

  // Capture snapshot on mount (after loading) so the document always has an up-to-date image
  // even when the user is on a different step.
  useEffect(() => {
    if (!hasLoadedFromLocalStorage || shapes.length === 0 || !problemId) return;
    // Use rAF to wait for SVG paint before capturing
    const rafId = requestAnimationFrame(() => {
      if (!svgRef.current) return;
      captureSnapshotAsync(svgRef.current, shapes, (snapshot) => {
        if (snapshot) {
          const attemptKey = `hld-attempt-${problemId}`;
          try {
            const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
            attemptData.diagramSnapshot = snapshot;
            localStorage.setItem(attemptKey, JSON.stringify(attemptData));
          } catch (e) {}
          window.dispatchEvent(new CustomEvent('hldDiagramSnapshotUpdated', {
            detail: { problemId, snapshot }
          }));
        }
      });
    });
    return () => cancelAnimationFrame(rafId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLoadedFromLocalStorage, problemId]); // run once when loading completes

  // Notify parent component about unsaved changes status
  useEffect(() => {
    onUnsavedChangesChange(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChangesChange]);

  // Warn user before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        const message = 'You have not saved the HLD diagram. Are you sure you want to leave? Your changes may be lost.';
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Load problem data and drawing - only run once per problemId
  useEffect(() => {
    // Prevent multiple fetches for the same problemId
    if (hasInitialized && problemId) {
      return;
    }

    const fetchData = async () => {
      if (!problemId) {
        setLoading(false);
        return;
      }

      try {
        // Load drawing from backend only - don't use localStorage for drawing
        // Check old keys for one-time cleanup (delete them)
        const oldDrawingKey = `hld-drawing-${problemId}`;
        const oldDrawingData = localStorage.getItem(oldDrawingKey);
        
        // Clean up old drawing key if it exists
        if (oldDrawingData) {
          localStorage.removeItem(oldDrawingKey);
        }
        
        // First, try to load from localStorage (includes user's latest changes from previous session)
        const attemptKey = `hld-attempt-${problemId}`;
        const attemptData = localStorage.getItem(attemptKey);
        let drawing = null;
        
        if (attemptData) {
          try {
            const parsed = JSON.parse(attemptData);
            // Check if drawing exists in localStorage (includes user's changes saved on every action)
            drawing = parsed.drawing || parsed.steps?.hldDiagram?.drawing;
            if (drawing && drawing.shapes && Array.isArray(drawing.shapes)) {
              setShapes(drawing.shapes);
              setConnections(drawing.connections || []);
              setHasUnsavedChanges(false);
              setHasLoadedFromLocalStorage(true);
              // Load from localStorage first for immediate display (preserves user's changes)
              // Don't fetch from backend to avoid overwriting user's unsaved changes
              return;
            }
          } catch (e) {
            console.error('Failed to parse HLD attempt data from localStorage:', e);
          }
        }
        
        // If no cached drawing, load from backend
        const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
        if (userId) {
          try {
            const { getRequest } = await import('../../Fetch/request');
            const drawingRes = await getRequest(
              `${process.env.REACT_APP_URL}/hld-attempt?userId=${userId}&hldProblemId=${problemId}`
            );
            
            if (drawingRes?.status === '200' && drawingRes.response) {
              // Backend response has hldDiagram with shapes and connections
              const backendDrawing = drawingRes.response.hldDiagram || drawingRes.response.drawing;
              if (backendDrawing && backendDrawing.shapes && Array.isArray(backendDrawing.shapes)) {
                drawing = {
                  shapes: backendDrawing.shapes,
                  connections: backendDrawing.connections || []
                };
                
                // Save to localStorage for caching (but don't save when user draws - only on backend load)
                if (attemptData) {
                  const parsed = JSON.parse(attemptData);
                  parsed.drawing = drawing;
                  if (!parsed.steps) {
                    parsed.steps = {};
                  }
                  parsed.steps.hldDiagram = { drawing };
                  parsed.timestamp = Date.now();
                  localStorage.setItem(attemptKey, JSON.stringify(parsed));
                } else {
                  // Create new attempt data with drawing
                  const newAttemptData = {
                    drawing,
                    steps: {
                      hldDiagram: { drawing }
                    },
                    timestamp: Date.now()
                  };
                  localStorage.setItem(attemptKey, JSON.stringify(newAttemptData));
                }
                
                setShapes(drawing.shapes);
                setConnections(drawing.connections || []);
                setHasUnsavedChanges(false);
                setHasLoadedFromLocalStorage(true);
              } else {
                // No drawing in backend - set to empty
                setShapes([]);
                setConnections([]);
                setHasUnsavedChanges(false);
                setHasLoadedFromLocalStorage(true);
              }
            } else {
              // No drawing in backend - set to empty
              setShapes([]);
              setConnections([]);
              setHasUnsavedChanges(false);
              setHasLoadedFromLocalStorage(true);
            }
          } catch (backendError) {
            console.error('Failed to load HLD drawing from backend:', backendError);
            // On error, set to empty
            setShapes([]);
            setConnections([]);
            setHasUnsavedChanges(false);
            setHasLoadedFromLocalStorage(true);
          }
        } else {
          // No userId - set to empty
          setShapes([]);
          setConnections([]);
          setHasUnsavedChanges(false);
          setHasLoadedFromLocalStorage(true);
        }

        // Problem is now synced via useEffect above, so we don't need to set it here
        // But we'll set it here as a fallback if useEffect hasn't run yet
        if (propProblem && !problem) {
          setProblem(propProblem);
        } else if (!problem && problemId) {
          // Try to get problem details from localStorage as fallback
          const problemKey = `hld-problem-${problemId}`;
          const problemData = localStorage.getItem(problemKey);
          if (problemData) {
            try {
              const parsed = JSON.parse(problemData);
              setProblem(parsed);
            } catch (e) {
              console.error('Failed to parse HLD problem data from localStorage:', e);
              // Fallback: Set basic problem object
              setProblem({
                problemId: problemId,
                title: 'HLD Problem',
                description: 'High-Level Design Problem'
              });
            }
          } else {
            // Fallback: Set basic problem object to prevent "Problem not found"
            setProblem({
              problemId: problemId,
              title: 'HLD Problem',
              description: 'High-Level Design Problem'
            });
          }
        }

        // Drawing is already loaded from main attempt key above
        // No need to fetch separately - HLDProblemDetails already loaded it from backend
        // and stored it in the main attempt key
      } catch (error) {
        // Error fetching data
      } finally {
        setLoading(false);
        setHasInitialized(true);
      }
    };

    fetchData();
  }, [problemId, hasInitialized]);

  const [resizing, setResizing] = useState(null);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [initialSize, setInitialSize] = useState({ width: 0, height: 0 });
  const [initialMousePos, setInitialMousePos] = useState({ x: 0, y: 0 });

  // Excalidraw-style drawing states
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingStart, setDrawingStart] = useState({ x: 0, y: 0 });
  const [drawingCurrent, setDrawingCurrent] = useState({ x: 0, y: 0 });
  const [selectionBox, setSelectionBox] = useState(null); // { x, y, width, height }
  const [isSelecting, setIsSelecting] = useState(false);

  const [selectedConnection, setSelectedConnection] = useState(null);
  const [showComponentDropdown, setShowComponentDropdown] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [componentSearchTerm, setComponentSearchTerm] = useState('');
  const [editingShape, setEditingShape] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [editingCell, setEditingCell] = useState(null); // { tableId, rowIndex (-1=header), colIndex }
  const [editingCellText, setEditingCellText] = useState('');
  const [tableMenu, setTableMenu] = useState(null); // { tableId, type:'col'|'row', index, x, y }
  const [editingPosition, setEditingPosition] = useState({ x: 0, y: 0 });
  const [editingCenter, setEditingCenter] = useState({ x: 0, y: 0 }); // Store original center when editing starts
  const [textareaHeight, setTextareaHeight] = useState(16);
  const [containerWidth, setContainerWidth] = useState(200);
  const [editingTextareaHeight, setEditingTextareaHeight] = useState(50);
  const [editingTextareaWidth,  setEditingTextareaWidth]  = useState(200);
  const textMeasureRef = useRef(null);
  const textareaRef = useRef(null);
  
  // API Block child box dragging state
  const [draggedChildBox, setDraggedChildBox] = useState(null);
  const [childBoxDragOffset, setChildBoxDragOffset] = useState({ x: 0, y: 0 });
  const [editingChildBox, setEditingChildBox] = useState(null); // { apiBlockId, boxType }

  // Measure text width dynamically when editing text - width increases as user types
  useEffect(() => {
    if (editingShape && textMeasureRef.current) {
      const lines = editingText.split('\n');
      const longestLine = lines.reduce((longest, line) => 
        line.length > longest.length ? line : longest, '');
      
      // Update the hidden span content - use space if empty to measure one character width
      textMeasureRef.current.textContent = longestLine || ' ';
      
      // Measure after DOM update
      requestAnimationFrame(() => {
        if (textMeasureRef.current) {
          const measuredWidth = textMeasureRef.current.offsetWidth;
          // Width should be at least one character width (measure 'M' if empty), add 8px padding (4px on each side)
          if (measuredWidth === 0 && longestLine === '') {
            // Measure one character 'M' (widest) for minimum width
            textMeasureRef.current.textContent = 'M';
            const oneCharWidth = textMeasureRef.current.offsetWidth;
            textMeasureRef.current.textContent = ' '; // Reset
            const newWidth = Math.max(oneCharWidth, oneCharWidth + 8);
            setContainerWidth(newWidth);
          } else {
            const newWidth = Math.max(16, measuredWidth + 8);
            setContainerWidth(newWidth);
          }
        }
      });
    }
  }, [editingText, editingShape]);

  // Update textarea height when editing text changes to ensure all text is visible
  useEffect(() => {
    if (editingShape && textareaRef.current) {
      const textarea = textareaRef.current;
      // Reset height to auto to get accurate scrollHeight
      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;
      const minHeight = FIXED_FONT_SIZE * 1.2;
      const newHeight = Math.max(scrollHeight, minHeight);
      textarea.style.height = newHeight + 'px';
      setEditingTextareaHeight(newHeight);
    }
  }, [editingText, editingShape]);

  // HLD Components definition
  const HLD_COMPONENTS = {
    // Infrastructure Components
    'Load Balancer': { icon: FaNetworkWired, color: '#3b82f6', category: 'Infrastructure' },
    'API Gateway': { icon: FaShieldAlt, color: '#10b981', category: 'Infrastructure' },
    'CDN': { icon: FaGlobe, color: '#f59e0b', category: 'Infrastructure' },
    'Firewall': { icon: FaLock, color: '#ef4444', category: 'Infrastructure' },
    
    // Database Components
    'Primary Database': { icon: FaDatabase, color: '#8b5cf6', category: 'Database' },
    'Read Replica': { icon: FaDatabase, color: '#06b6d4', category: 'Database' },
    'Cache (Redis)': { icon: FaDatabase, color: '#dc2626', category: 'Database' },
    'Search Engine': { icon: FaSearch, color: '#059669', category: 'Database' },
    'Message Queue': { icon: FaExchangeAlt, color: '#7c3aed', category: 'Database' },
    
    // Application Components
    'Web Server': { icon: FaServer, color: '#0891b2', category: 'Application' },
    'Application Server': { icon: FaCogs, color: '#65a30d', category: 'Application' },
    'Microservice': { icon: FaRocket, color: '#ea580c', category: 'Application' },
    'Background Job': { icon: FaCog, color: '#6b7280', category: 'Application' },
    'Cron Job': { icon: FaClock, color: '#7c3aed', category: 'Application' },
    'Producer': { icon: FaArrowsAltH, color: '#059669', category: 'Messaging' },
    'Consumer': { icon: FaArrowsAltH, color: '#0891b2', category: 'Messaging' },
    'Producer/Consumer': { icon: FaArrowsAltH, color: '#d97706', category: 'Messaging' },
    
    // External Services
    'Third Party API': { icon: FaCloud, color: '#8b5a2b', category: 'External' },
    'Payment Gateway': { icon: FaCreditCard, color: '#059669', category: 'External' },
    'Email Service': { icon: FaEnvelope, color: '#7c2d12', category: 'External' },
    'SMS Service': { icon: FaMobile, color: '#1e40af', category: 'External' },
    
    // User Interface
    'Web Client': { icon: FaDesktop, color: '#dc2626', category: 'UI' },
    'Mobile App': { icon: FaMobile, color: '#7c3aed', category: 'UI' },
    'Admin Panel': { icon: FaUsers, color: '#059669', category: 'UI' },
    
    // Monitoring & Analytics
    'Monitoring': { icon: FaChartLine, color: '#0891b2', category: 'Monitoring' },
    'Logging': { icon: FaFileAlt, color: '#6b7280', category: 'Monitoring' },
    'Analytics': { icon: FaChartLine, color: '#7c2d12', category: 'Monitoring' },
    
    // Security & Auth
    'Authentication': { icon: FaKey, color: '#dc2626', category: 'Security' },
    'Authorization': { icon: FaUserShield, color: '#059669', category: 'Security' },
    'OAuth Provider': { icon: FaUserCheck, color: '#7c3aed', category: 'Security' },
    
    // Storage
    'File Storage': { icon: FaFileAlt, color: '#8b5cf6', category: 'Storage' },
    'Object Storage': { icon: FaLayerGroup, color: '#06b6d4', category: 'Storage' },
    'Block Storage': { icon: FaDatabase, color: '#0891b2', category: 'Storage' },
    
    // Networking
    'VPN': { icon: FaNetworkWired, color: '#1e40af', category: 'Networking' },
    'DNS': { icon: FaGlobe, color: '#7c2d12', category: 'Networking' },
    
    // Messaging & Communication
    'Kafka': { icon: FaExchangeAlt, color: '#000000', category: 'Messaging' },
    'RabbitMQ': { icon: FaExchangeAlt, color: '#ff6600', category: 'Messaging' },
    'WebSocket': { icon: FaNetworkWired, color: '#06b6d4', category: 'Messaging' },
    'Webhook': { icon: FaBell, color: '#7c3aed', category: 'Messaging' },
    
    // Cloud Services
    'AWS Lambda': { icon: FaCloud, color: '#ff9900', category: 'Cloud' },
    'AWS S3': { icon: FaCloud, color: '#ff9900', category: 'Cloud' },
    'AWS EC2': { icon: FaServer, color: '#ff9900', category: 'Cloud' },
    'AWS RDS': { icon: FaDatabase, color: '#ff9900', category: 'Cloud' },
    'AWS ElastiCache': { icon: FaDatabase, color: '#ff9900', category: 'Cloud' },
    'AWS SQS': { icon: FaExchangeAlt, color: '#ff9900', category: 'Cloud' },
    'AWS SNS': { icon: FaBell, color: '#ff9900', category: 'Cloud' },
    'AWS CloudFront': { icon: FaGlobe, color: '#ff9900', category: 'Cloud' },
    'AWS API Gateway': { icon: FaShieldAlt, color: '#ff9900', category: 'Cloud' },
    'AWS Load Balancer': { icon: FaNetworkWired, color: '#ff9900', category: 'Cloud' },
    
    // Google Cloud
    'GCP Compute Engine': { icon: FaServer, color: '#4285f4', category: 'Cloud' },
    'GCP Cloud Storage': { icon: FaCloud, color: '#4285f4', category: 'Cloud' },
    'GCP Cloud SQL': { icon: FaDatabase, color: '#4285f4', category: 'Cloud' },
    'GCP Pub/Sub': { icon: FaExchangeAlt, color: '#4285f4', category: 'Cloud' },
    'GCP Cloud Functions': { icon: FaCloud, color: '#4285f4', category: 'Cloud' },
    
    // Azure
    'Azure VM': { icon: FaServer, color: '#0078d4', category: 'Cloud' },
    'Azure Blob Storage': { icon: FaCloud, color: '#0078d4', category: 'Cloud' },
    'Azure SQL Database': { icon: FaDatabase, color: '#0078d4', category: 'Cloud' },
    'Azure Service Bus': { icon: FaExchangeAlt, color: '#0078d4', category: 'Cloud' },
    
    // Specialized Services
    'Elasticsearch': { icon: FaSearch, color: '#f7df1e', category: 'Search' },
    'MongoDB': { icon: FaDatabase, color: '#47a248', category: 'Database' },
    'PostgreSQL': { icon: FaDatabase, color: '#336791', category: 'Database' },
    'MySQL': { icon: FaDatabase, color: '#00758f', category: 'Database' },
    'Redis': { icon: FaDatabase, color: '#dc382d', category: 'Cache' },
    'Memcached': { icon: FaDatabase, color: '#c8172b', category: 'Cache' },
    'Nginx': { icon: FaServer, color: '#009639', category: 'Web Server' },
    'Apache': { icon: FaServer, color: '#d22128', category: 'Web Server' },
    'Docker': { icon: FaCog, color: '#2496ed', category: 'Containerization' },
    'Kubernetes': { icon: FaCogs, color: '#326ce5', category: 'Containerization' }
  };







  // ── saveHistory: snapshot current state before a mutation ────────────────
  const saveHistory = useCallback(() => {
    const snapshot = {
      shapes: JSON.parse(JSON.stringify(shapesRef.current)),
      connections: JSON.parse(JSON.stringify(connectionsRef.current)),
    };
    // Drop any redo states ahead of current index
    historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
    historyRef.current.push(snapshot);
    if (historyRef.current.length > 50) historyRef.current.shift();
    historyIndexRef.current = historyRef.current.length - 1;
    setHistoryVersion((v) => v + 1);
  }, []);

  const undo = useCallback(() => {
    if (historyIndexRef.current <= 0) return;
    historyIndexRef.current -= 1;
    const snapshot = historyRef.current[historyIndexRef.current];
    setShapes(snapshot.shapes);
    setConnections(snapshot.connections);
    setHasUnsavedChanges(true);
    setHistoryVersion((v) => v + 1);
  }, []);

  const redo = useCallback(() => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    historyIndexRef.current += 1;
    const snapshot = historyRef.current[historyIndexRef.current];
    setShapes(snapshot.shapes);
    setConnections(snapshot.connections);
    setHasUnsavedChanges(true);
    setHistoryVersion((v) => v + 1);
  }, []);

  const deleteSelected = () => {
    saveHistory();
    const selectedShapeIds = shapes.filter((s) => s.selected).map((s) => s.id);
    setShapes((prev) => prev.filter((s) => !s.selected));
    setConnections((prev) => prev.filter((c) => !selectedShapeIds.includes(c.from) && !selectedShapeIds.includes(c.to)));

    // Also delete selected connection
    if (selectedConnection) {
      setConnections((prev) => prev.filter((c) => c.id !== selectedConnection));
      setSelectedConnection(null);
    }
    setHasUnsavedChanges(true);
  };

  const copySelected = useCallback(() => {
    const selectedShapes = shapes.filter((s) => s.selected);
    if (selectedShapes.length === 0) {
      return;
    }

    const selectedShapeIds = selectedShapes.map((s) => s.id);
    
    // Copy shapes (remove selected property and create deep copy)
    const shapesToCopy = selectedShapes.map(shape => ({
      ...shape,
      selected: false
    }));
    
    // Copy connections that are between selected shapes
    const connectionsToCopy = connections.filter(
      (c) => selectedShapeIds.includes(c.from) && selectedShapeIds.includes(c.to)
    ).map(conn => ({ ...conn }));

    setCopiedShapes(shapesToCopy);
    setCopiedConnections(connectionsToCopy);
    setSnackbar({ open: true, message: `Copied ${selectedShapes.length} component(s)`, severity: 'success' });
  }, [shapes, connections]);

  const pasteCopied = useCallback(() => {
    if (copiedShapes.length === 0) {
      return;
    }

    const offsetX = 50;
    const offsetY = 50;
    const idMap = new Map(); // Map old IDs to new IDs

    // Create new shapes with offset positions and new IDs
    const newShapes = copiedShapes.map((shape) => {
      const newId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
      idMap.set(shape.id, newId);
      
      return {
        ...shape,
        id: newId,
        position: {
          x: shape.position.x + offsetX,
          y: shape.position.y + offsetY
        },
        selected: true // Select pasted shapes
      };
    });

    // Create new connections with updated IDs
    const newConnections = copiedConnections.map((conn) => {
      const newId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
      return {
        ...conn,
        id: newId,
        from: idMap.get(conn.from) || conn.from,
        to: idMap.get(conn.to) || conn.to
      };
    });

    saveHistory();

    // Deselect all existing shapes
    setShapes((prev) => prev.map((s) => ({ ...s, selected: false })));

    // Add new shapes
    setShapes((prev) => [...prev, ...newShapes]);

    // Add new connections
    setConnections((prev) => [...prev, ...newConnections]);

    setHasUnsavedChanges(true);
    setSnackbar({ open: true, message: `Pasted ${newShapes.length} component(s)`, severity: 'success' });
  }, [copiedShapes, copiedConnections, saveHistory]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Handle undo (Ctrl+Z or Cmd+Z)
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === 'z') {
        if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
          e.preventDefault();
          undo();
          return;
        }
      }

      // Handle redo (Ctrl+Y or Ctrl+Shift+Z or Cmd+Shift+Z)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'z'))) {
        if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
          e.preventDefault();
          redo();
          return;
        }
      }

      // Handle copy (Ctrl+C or Cmd+C)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        // Only copy if not in text input
        if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
          e.preventDefault();
          copySelected();
          return;
        }
      }

      // Handle paste (Ctrl+V or Cmd+V)
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        // Only paste if not in text input
        if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
          e.preventDefault();
          pasteCopied();
          return;
        }
      }

      if (e.key === "Escape") {
        setShapes((prev) => prev.map((s) => ({ ...s, selected: false })));
        setConnectionStart(null);
        setSelectedTool("select");
        setResizing(null);
        setResizeHandle(null);
        setDraggedShape(null);
        setSelectedConnection(null);
        setShowComponentDropdown(false);
        setComponentSearchTerm('');
        setEditingShape(null);
        setEditingText('');
      }
      // Keyboard delete functionality removed - use delete button instead
      // if (e.key === "Delete" || e.key === "Backspace") {
      //   deleteSelected();
      // }
    };

    const handleGlobalMouseUp = () => {
      setResizing(null);
      setResizeHandle(null);
      setDraggedShape(null);
      setDragOffset({ x: 0, y: 0 });
      // Don't clear selectedConnection here - let it persist
    };

    const handleClickOutside = (e) => {
      if (showComponentDropdown && !e.target.closest('.component-dropdown')) {
        setShowComponentDropdown(false);
        setComponentSearchTerm('');
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mouseup", handleGlobalMouseUp);
    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mouseup", handleGlobalMouseUp);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showComponentDropdown, copySelected, pasteCopied, undo, redo]);

  const getShapeCenter = (shape) => {
    return {
      x: shape.position.x + shape.size.width / 2,
      y: shape.position.y + shape.size.height / 2,
    };
  };

  // Helper function to bring selected shapes to the front (end of array)
  const bringSelectedToFront = useCallback((shapesArray) => {
    const selected = shapesArray.filter(s => s.selected);
    const unselected = shapesArray.filter(s => !s.selected);
    // Selected shapes go to the end (rendered on top)
    return [...unselected, ...selected];
  }, []);

  // Excalidraw-style: Handle text tool - single click to place and edit
  const handleSvgClick = useCallback(
    (e) => {
      if (!svgRef.current) return;

      // Close any open table dropdown
      setTableMenu(null);

      // Check if we clicked on a connection (the target might be a line or g element)
      const target = e.target;
      if (target.tagName === 'line' || target.closest('g')) {
        // Don't clear connection selection if clicking on a connection
        return;
      }

      // Clear connection selection when clicking on empty space
      setSelectedConnection(null);
      
      // Component placement: Drop selected component on canvas
      if (selectedComponent) {
        const rect = svgRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        const componentSize = { width: 80, height: 80 };
        const newShape = {
          id: Date.now().toString(),
          type: 'component',
          componentType: selectedComponent,
          position: {
            x: x - componentSize.width / 2,
            y: y - componentSize.height / 2
          },
          size: componentSize,
          selected: false,
          text: '',
        };
        saveHistory();
        setShapes((prev) => [...prev, newShape]);
        setHasUnsavedChanges(true);
        setSelectedComponent(null);
        setSelectedTool("select");
        return;
      }
      
      // Rectangle/Text box placement: Create empty box where user can write
      if (selectedTool === "rectangle") {
        const rect = svgRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        const boxSize = { width: 200, height: 50 };
        const newShapeId = Date.now().toString();
        const newShape = {
          id: newShapeId,
          type: 'rectangle',
          position: {
            x: x - boxSize.width / 2,
            y: y - boxSize.height / 2
          },
          size: boxSize,
          selected: false,
          text: '',
        };
        saveHistory();
        setShapes((prev) => [...prev, newShape]);
        setHasUnsavedChanges(true);
        
        // Automatically start editing the new rectangle
        setSelectedTool("select");
        setTimeout(() => {
          const centerX = newShape.position.x + newShape.size.width / 2;
          const centerY = newShape.position.y + newShape.size.height / 2;
          setEditingCenter({ x: centerX, y: centerY });
          setEditingShape(newShapeId);
          setEditingText('');
          setEditingPosition({ x: centerX, y: centerY });
          setEditingTextareaHeight(16); // Start with minimum height
        }, 0);

        return;
      }

      // Oval/Circle placement: Create empty circle where user can write (becomes oval as text grows)
      if (selectedTool === "oval") {
        const rect = svgRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        // Start with a circle (equal width and height)
        const circleSize = 120; // Start as a circle
        const ovalSize = { width: circleSize, height: circleSize };
        const newShapeId = Date.now().toString();
        const newShape = {
          id: newShapeId,
          type: 'oval',
          position: {
            x: x - ovalSize.width / 2,
            y: y - ovalSize.height / 2
          },
          size: ovalSize,
          selected: false,
          text: '',
        };
        saveHistory();
        setShapes((prev) => [...prev, newShape]);
        setHasUnsavedChanges(true);
        
        // Automatically start editing the new oval
        setSelectedTool("select");
        setTimeout(() => {
          const centerX = newShape.position.x + newShape.size.width / 2;
          const centerY = newShape.position.y + newShape.size.height / 2;
          setEditingCenter({ x: centerX, y: centerY });
          setEditingShape(newShapeId);
          setEditingText('');
          setEditingPosition({ x: centerX, y: centerY });
          setEditingTextareaHeight(16); // Start with minimum height
        }, 0);

        return;
      }

      // API Block placement: Create API block with header
      if (selectedTool === "apiBlock") {
        const rect = svgRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        const apiBlockSize = { width: 300, height: 150 }; // Fixed width, initial height
        const headerHeight = 40;
        const newShapeId = Date.now().toString();
        const newShape = {
          id: newShapeId,
          type: 'apiBlock',
          position: {
            x: x - apiBlockSize.width / 2,
            y: y - apiBlockSize.height / 2
          },
          size: apiBlockSize,
          selected: false,
          headerHeight: headerHeight,
          children: [], // Array of child boxes: { type: 'params'|'body'|'header', position: {x, y}, size: {width, height}, text: '' }
        };
        saveHistory();
        setShapes((prev) => [...prev, newShape]);
        setHasUnsavedChanges(true);
        setSelectedTool("select"); // Switch back to select tool after placing
        return;
      }

      if (selectedTool === "table") {
        const rect = svgRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const newShape = {
          id: Date.now().toString(),
          type: 'table',
          position: { x: x - 150, y: y - 75 },
          size: { width: 300, height: 120 },
          columns: ['Column 1', 'Column 2', 'Column 3'],
          rows: [['', '', ''], ['', '', ''], ['', '', '']],
          selected: false,
        };
        saveHistory();
        setShapes((prev) => [...prev, newShape]);
        setHasUnsavedChanges(true);
        setSelectedTool("select");
        return;
      }

      // Select tool: Clear selections on empty space click (unless we're selecting)
      if (selectedTool === "select" && !isSelecting) {
        setShapes((prev) => prev.map((s) => ({ ...s, selected: false })));
      }
    },
    [selectedTool, isSelecting, selectedComponent, shapes],
  );

  // Excalidraw-style: Handle double-click on shapes to edit text
  const handleSvgDoubleClick = useCallback(
    (e) => {
      if (!svgRef.current) return;

      // Double-click on shapes to edit text (only for select tool)
      if (selectedTool === "select") {
        const target = e.target;
        const shapeElement = target.closest('g[data-shape-id]');
        if (shapeElement) {
          const shapeId = shapeElement.getAttribute('data-shape-id');
          const shape = shapes.find(s => s.id === shapeId);
          if (shape && (shape.type === 'rectangle' || shape.type === 'oval' || shape.type === 'textAnnotation')) {
            e.stopPropagation();
            handleShapeDoubleClick(e, shapeId);
          }
        } else {
          // Double-click on empty canvas → create a text annotation
          e.stopPropagation();
          const svgRect = svgRef.current.getBoundingClientRect();
          const container = svgContainerRef.current;
          const scrollLeft = container ? container.scrollLeft : 0;
          const scrollTop  = container ? container.scrollTop  : 0;
          const x = e.clientX - svgRect.left + scrollLeft;
          const y = e.clientY - svgRect.top  + scrollTop;
          const newId = Date.now().toString();
          const newAnnotation = {
            id: newId,
            type: 'textAnnotation',
            position: { x, y },
            size: { width: 200, height: 30 },
            text: '',
            selected: true,
          };
          saveHistory();
          setShapes(prev => [...prev.map(s => ({ ...s, selected: false })), newAnnotation]);
          setHasUnsavedChanges(true);
          setEditingShape(newId);
          setEditingText('');
          setEditingCenter({ x, y });
          setEditingTextareaHeight(30);
        }
      }
    },
    [selectedTool, shapes, saveHistory],
  );

  const handleShapeDoubleClick = useCallback(
    (e, shapeId) => {
      e.stopPropagation();
      if (selectedTool === "select") {
        setShapes((prev) =>
          prev.map((s) => ({
            ...s,
            selected: s.id === shapeId,
          })),
        );
        // Clear connection selection when selecting a shape
        setSelectedConnection(null);
        
        // Start editing text for rectangle, oval, or textAnnotation
        const shape = shapes.find(s => s.id === shapeId);
        if (shape && (shape.type === 'rectangle' || shape.type === 'oval')) {
          const centerX = shape.position.x + shape.size.width / 2;
          const centerY = shape.position.y + shape.size.height / 2;
          setEditingCenter({ x: centerX, y: centerY });
          setEditingShape(shapeId);
          setEditingText(shape.text || '');
          setEditingPosition({ x: centerX, y: centerY });
          // Initialize textarea height based on text content
          if (shape.text) {
            const lines = shape.text.split('\n');
            const lineCount = lines.length;
            const estimatedHeight = Math.max(16, lineCount * FIXED_FONT_SIZE * 1.2);
            setEditingTextareaHeight(estimatedHeight);
          } else {
            setEditingTextareaHeight(16);
          }
        } else if (shape && shape.type === 'textAnnotation') {
          setEditingShape(shapeId);
          setEditingText(shape.text || '');
          setEditingCenter({ x: shape.position.x, y: shape.position.y });
          const lines = (shape.text || '').split('\n');
          setEditingTextareaHeight(Math.max(30, lines.length * 20));
          setEditingTextareaWidth(shape.size.width || 200);
        }
      }
    },
    [selectedTool, shapes],
  );

  // Excalidraw-style: Handle mouse move for drawing (defined early for use in handleMouseMove)
  const handleSvgMouseMove = useCallback(
    (e) => {
      if (!svgRef.current) return;
      
      const rect = svgRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Update drawing current position
      if (isDrawing) {
        setDrawingCurrent({ x, y });
      }
      
      // Update selection box
      if (isSelecting) {
        const width = x - drawingStart.x;
        const height = y - drawingStart.y;
        setSelectionBox({
          x: width < 0 ? x : drawingStart.x,
          y: height < 0 ? y : drawingStart.y,
          width: Math.abs(width),
          height: Math.abs(height),
        });
        
        // Select shapes within selection box
        setShapes((prev) => {
          const updated = prev.map((shape) => {
            const shapeCenterX = shape.position.x + shape.size.width / 2;
            const shapeCenterY = shape.position.y + shape.size.height / 2;
            const box = {
              x: width < 0 ? x : drawingStart.x,
              y: height < 0 ? y : drawingStart.y,
              width: Math.abs(width),
              height: Math.abs(height),
            };
            const isInside =
              shapeCenterX >= box.x &&
              shapeCenterX <= box.x + box.width &&
              shapeCenterY >= box.y &&
              shapeCenterY <= box.y + box.height;
            return { ...shape, selected: isInside };
          });
          // Bring selected shapes to front
          return bringSelectedToFront(updated);
        });
      }
    },
    [isDrawing, isSelecting, drawingStart, selectedTool, bringSelectedToFront],
  );
  
  // Excalidraw-style: Handle mouse up to finish drawing (defined early for use in handleMouseUp)
  const handleSvgMouseUp = useCallback(
    (e) => {
      if (!svgRef.current) return;

      // Finish selection box
      if (isSelecting) {
        setIsSelecting(false);
        setSelectionBox(null);
        // Populate initialSelectedPositionsRef so drag works immediately after rubber-band select
        setShapes((prev) => {
          initialSelectedPositionsRef.current.clear();
          prev.filter((s) => s.selected).forEach((s) => {
            initialSelectedPositionsRef.current.set(s.id, { x: s.position.x, y: s.position.y });
          });
          return prev;
        });
      }
    },
    [isDrawing, isSelecting, selectedTool, drawingStart, drawingCurrent],
  );

  // Excalidraw-style: Handle mouse down on SVG canvas for drawing
  const handleSvgMouseDown = useCallback(
    (e) => {
      if (!svgRef.current) return;
      if (editingShape || editingCell) return; // Don't start drawing if editing text or a table cell
      
      const rect = svgRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Check if clicking on a shape or connection
      const target = e.target;
      if (target.tagName === 'line' || target.closest('g[data-shape-id]')) {
        return; // Let shape handlers deal with it
      }
      
      // Component placement: handled in handleSvgClick, don't interfere here
      if (selectedComponent) {
        return; // Component placement is handled in handleSvgClick
      }
      
      // Rectangle tool: handled in handleSvgClick, don't interfere here
      if (selectedTool === "rectangle") {
        return; // Rectangle placement is handled in handleSvgClick
      }
      
      // Oval tool: handled in handleSvgClick, don't interfere here
      if (selectedTool === "oval") {
        return; // Oval placement is handled in handleSvgClick
      }
      
      // Select tool: Start selection box
      if (selectedTool === "select") {
        setIsSelecting(true);
        setSelectionBox({ x, y, width: 0, height: 0 });
        setDrawingStart({ x, y });
        setDrawingCurrent({ x, y });
        return;
      }
    },
    [selectedTool, editingShape, selectedComponent, shapes],
  );
  

  const handleShapeMouseDown = useCallback(
    (e, shapeId) => {
      e.stopPropagation();
      if (selectedTool === "line") {
        if (!connectionStart) {
          setConnectionStart(shapeId);
          setShapes((prev) => {
            const updated = prev.map((s) => ({ ...s, selected: s.id === shapeId }));
            return bringSelectedToFront(updated);
          });
        } else if (connectionStart !== shapeId) {
          const newConnection = {
            id: Date.now().toString(),
            from: connectionStart,
            to: shapeId,
          };
          saveHistory();
          setConnections((prev) => [...prev, newConnection]);
          setHasUnsavedChanges(true);
          setConnectionStart(null);
          setShapes((prev) => prev.map((s) => ({ ...s, selected: false })));
        }
        return;
      }

      if (selectedTool === "select") {
        const rect = svgRef.current?.getBoundingClientRect();
        if (!rect) return;
        const shape = shapes.find((s) => s.id === shapeId);
        if (!shape) return;

        // Handle multi-select with Ctrl/Cmd or Shift key
        const isMultiSelect = e.ctrlKey || e.metaKey || e.shiftKey;
        
        if (isMultiSelect) {
          // Toggle selection for this shape (multi-select mode)
          const willBeSelected = !shape.selected;
          setShapes((prev) => {
            const updated = prev.map((s) =>
              s.id === shapeId ? { ...s, selected: willBeSelected } : s
            );
            // Store initial positions of all selected shapes after update
            const selectedShapes = updated.filter((s) => s.selected);
            initialSelectedPositionsRef.current.clear();
            selectedShapes.forEach((s) => {
              initialSelectedPositionsRef.current.set(s.id, { x: s.position.x, y: s.position.y });
            });
            // Bring selected shapes to front
            return bringSelectedToFront(updated);
          });
          // Set up for dragging if the shape becomes selected
          if (willBeSelected) {
            saveHistory();
            setDraggedShape(shapeId);
            setDragOffset({
              x: e.clientX - rect.left - shape.position.x,
              y: e.clientY - rect.top - shape.position.y,
            });
          } else {
            // If shape was deselected, clear drag if it was the dragged shape
            if (draggedShape === shapeId) {
              setDraggedShape(null);
            }
          }
        } else {
          // If the clicked shape is already part of a multi-selection, preserve the
          // selection and just start a group drag — don't clear other selected shapes.
          const selectedCount = shapes.filter((s) => s.selected).length;
          if (shape.selected && selectedCount > 1) {
            // Group drag: keep all selected, refresh initial positions
            saveHistory();
            initialSelectedPositionsRef.current.clear();
            shapes.filter((s) => s.selected).forEach((s) => {
              initialSelectedPositionsRef.current.set(s.id, { x: s.position.x, y: s.position.y });
            });
            setDraggedShape(shapeId);
            setDragOffset({
              x: e.clientX - rect.left - shape.position.x,
              y: e.clientY - rect.top - shape.position.y,
            });
          } else {
            // Single select mode - select only this shape
            setShapes((prev) => {
              const updated = prev.map((s) => ({
                ...s,
                selected: s.id === shapeId,
              }));
              // Store initial position of the selected shape
              initialSelectedPositionsRef.current.clear();
              const selectedShape = updated.find((s) => s.id === shapeId);
              if (selectedShape) {
                initialSelectedPositionsRef.current.set(selectedShape.id, {
                  x: selectedShape.position.x,
                  y: selectedShape.position.y,
                });
              }
              // Bring selected shape to front
              return bringSelectedToFront(updated);
            });
            // Set up for dragging
            saveHistory();
            setDraggedShape(shapeId);
            setDragOffset({
              x: e.clientX - rect.left - shape.position.x,
              y: e.clientY - rect.top - shape.position.y,
            });
          }
        }

        // Clear connection selection when interacting with a shape
        setSelectedConnection(null);
      }
    },
    [selectedTool, connectionStart, shapes, bringSelectedToFront, saveHistory],
  );

  const handleMouseMove = useCallback(
    (e) => {
      // Handle SVG-level drawing and selection first
      handleSvgMouseMove(e);
      
      if (resizing) {
        handleResizeMouseMove(e);
        return;
      }

      // Handle child box dragging
      if (draggedChildBox && svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        const newX = e.clientX - rect.left - childBoxDragOffset.x;
        const newY = e.clientY - rect.top - childBoxDragOffset.y;
        
        setShapes((prev) => {
          return prev.map((shape) => {
            if (shape.id === draggedChildBox.apiBlockId && shape.type === 'apiBlock' && shape.children) {
              const apiBlock = shape;
              const padding = 10;
              const headerHeight = apiBlock.headerHeight || 40;
              
              // Constrain child box within API block bounds
              const minX = apiBlock.position.x + padding;
              const maxX = apiBlock.position.x + apiBlock.size.width - padding;
              const minY = apiBlock.position.y + headerHeight + padding;
              const maxY = apiBlock.position.y + apiBlock.size.height - padding;
              
              const constrainedX = Math.max(minX, Math.min(maxX - (shape.children.find(c => c.type === draggedChildBox.boxType)?.size.width || 0), newX));
              const constrainedY = Math.max(minY, Math.min(maxY - (shape.children.find(c => c.type === draggedChildBox.boxType)?.size.height || 0), newY));
              
              return {
                ...shape,
                children: shape.children.map((childBox) => {
                  if (childBox.type === draggedChildBox.boxType) {
                    return {
                      ...childBox,
                      position: { x: constrainedX, y: constrainedY }
                    };
                  }
                  return childBox;
                })
              };
            }
            return shape;
          });
        });
        setHasUnsavedChanges(true);
        return;
      }

      if (!draggedShape || !svgRef.current) return;
      const rect = svgRef.current.getBoundingClientRect();
      const newX = e.clientX - rect.left - dragOffset.x;
      const newY = e.clientY - rect.top - dragOffset.y;

      // Get the initial position of the dragged shape
      const initialPos = initialSelectedPositionsRef.current.get(draggedShape);
      if (!initialPos) {
        // Fallback to single shape drag
        setShapes((prev) => {
          const updated = prev.map((shape) => (shape.id === draggedShape ? { ...shape, position: { x: newX, y: newY } } : shape));
          // Keep selected shape at front while dragging
          return bringSelectedToFront(updated);
        });
      } else {
        // Calculate offset from initial position
        const deltaX = newX - initialPos.x;
        const deltaY = newY - initialPos.y;

        // Move all selected shapes by the same offset
        setShapes((prev) => {
          const updated = prev.map((shape) => {
            if (shape.selected) {
              const initialShapePos = initialSelectedPositionsRef.current.get(shape.id);
              if (initialShapePos) {
                return {
                  ...shape,
                  position: {
                    x: initialShapePos.x + deltaX,
                    y: initialShapePos.y + deltaY,
                  },
                };
              }
            }
            return shape;
          });
          // Keep selected shapes at front while dragging
          return bringSelectedToFront(updated);
        });
      }
      setHasUnsavedChanges(true);
    },
    [draggedShape, dragOffset, resizing, handleSvgMouseMove, bringSelectedToFront, draggedChildBox, childBoxDragOffset],
  );

  const handleMouseUp = useCallback((e) => {
    // Handle SVG-level drawing and selection
    handleSvgMouseUp(e);
    
    setDraggedShape(null);
    setDragOffset({ x: 0, y: 0 });
    setDraggedChildBox(null);
    setChildBoxDragOffset({ x: 0, y: 0 });
    setResizing(null);
    setResizeHandle(null);
  }, [handleSvgMouseUp]);

  const handleResizeMouseDown = useCallback(
    (e, shapeId, handle) => {
      e.stopPropagation();
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;

      const shape = shapes.find((s) => s.id === shapeId);
      if (!shape) return;

      setResizing(shapeId);
      setResizeHandle(handle);
      setInitialSize({ ...shape.size });
      setInitialMousePos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    },
    [shapes],
  );

  const handleResizeMouseMove = useCallback(
    (e) => {
      if (!resizing || !svgRef.current) return;

      const rect = svgRef.current.getBoundingClientRect();
      const currentMouseX = e.clientX - rect.left;
      const currentMouseY = e.clientY - rect.top;

      const deltaX = currentMouseX - initialMousePos.x;
      const deltaY = currentMouseY - initialMousePos.y;

      setShapes((prev) =>
        prev.map((shape) => {
          if (shape.id !== resizing) return shape;

          const newSize = { ...shape.size };
          const newPosition = { ...shape.position };

          // Handle API block height-only resizing
          if (shape.type === "apiBlock" && resizeHandle === "bottom") {
            const headerHeight = shape.headerHeight || 40;
            const minHeight = headerHeight + 50; // Minimum height to show header
            newSize.height = Math.max(minHeight, initialSize.height + deltaY);
            // Width stays constant, position stays same
            return { ...shape, size: newSize };
          }

          switch (resizeHandle) {
            case "se":
              const minSizeWithPadding = 16; // Minimum size to maintain top and bottom padding (8px each)
              newSize.width = Math.max(minSizeWithPadding, initialSize.width + deltaX);
              newSize.height = Math.max(minSizeWithPadding, initialSize.height + deltaY);
              break;
            case "sw":
              const minSizeWithPaddingSW = 16; // Minimum size to maintain top and bottom padding
              newSize.width = Math.max(minSizeWithPaddingSW, initialSize.width - deltaX);
              newSize.height = Math.max(minSizeWithPaddingSW, initialSize.height + deltaY);
              newPosition.x = shape.position.x + (shape.size.width - newSize.width);
              break;
            case "ne":
              const minSizeWithPaddingNE = 16; // Minimum size to maintain top and bottom padding
              newSize.width = Math.max(minSizeWithPaddingNE, initialSize.width + deltaX);
              newSize.height = Math.max(minSizeWithPaddingNE, initialSize.height - deltaY);
              newPosition.y = shape.position.y + (shape.size.height - newSize.height);
              break;
            case "nw":
              const minSizeWithPaddingNW = 16; // Minimum size to maintain top and bottom padding
              newSize.width = Math.max(minSizeWithPaddingNW, initialSize.width - deltaX);
              newSize.height = Math.max(minSizeWithPaddingNW, initialSize.height - deltaY);
              newPosition.x = shape.position.x + (shape.size.width - newSize.width);
              newPosition.y = shape.position.y + (shape.size.height - newSize.height);
              break;
          }

          return { ...shape, size: newSize, position: newPosition };
        }),
      );
    },
    [resizing, resizeHandle, initialSize, initialMousePos],
  );

  const clearBoard = () => {
    saveHistory();
    setShapes([]);
    setConnections([]);
    setConnectionStart(null);
    setSelectedConnection(null);
    setHasUnsavedChanges(true);
  };

  const handleConnectionClick = useCallback(
    (e, connectionId) => {
      e.stopPropagation();
      e.preventDefault();
      if (selectedTool === "select") {
        setSelectedConnection(connectionId);
        // Clear shape selections when selecting a connection
        setShapes((prev) => prev.map((s) => ({ ...s, selected: false })));
      }
    },
    [selectedTool],
  );

  const deleteSelectedConnection = () => {
    if (selectedConnection) {
      setConnections((prev) => prev.filter((c) => c.id !== selectedConnection));
      setSelectedConnection(null);
      setHasUnsavedChanges(true);
    }
  };

  // Helper: capture SVG snapshot cropped to shapes bounding box.
  const captureSnapshotAsync = (svgEl, shapesList, callback) => {
    if (!svgEl || !shapesList || shapesList.length === 0) { callback(null); return; }
    const PAD = 40;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    shapesList.forEach(s => {
      const x = s.position?.x ?? 0;
      const y = s.position?.y ?? 0;
      const w = s.size?.width ?? 100;
      const h = s.size?.height ?? 100;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x + w > maxX) maxX = x + w;
      if (y + h > maxY) maxY = y + h;
    });
    if (minX === Infinity) { callback(null); return; }
    const vbX = minX - PAD, vbY = minY - PAD;
    const vbW = Math.max((maxX - minX) + PAD * 2, 100);
    const vbH = Math.max((maxY - minY) + PAD * 2, 100);
    try {
      const clone = svgEl.cloneNode(true);
      clone.setAttribute('viewBox', `${vbX} ${vbY} ${vbW} ${vbH}`);
      clone.setAttribute('width', String(vbW));
      clone.setAttribute('height', String(vbH));

      // Remove ALL foreignObject elements — they never render inside <img> SVG data URLs
      clone.querySelectorAll('foreignObject').forEach(fo => fo.remove());

      // Re-add shape text as native SVG <text> elements so they render in snapshots
      const svgNS = 'http://www.w3.org/2000/svg';
      const FONT_SIZE = 14;
      const LINE_H = FONT_SIZE * 1.4;

      shapesList.forEach(shape => {
        if (!shape || !shape.position || !shape.size) return;
        const { position, size, type } = shape;
        const shapeGroup = clone.querySelector(`g[data-shape-id="${shape.id}"]`);
        if (!shapeGroup) return;

        const centerX = position.x + size.width / 2;
        const centerY = position.y + size.height / 2;

        // Rectangle and oval shapes: render shape.text centered
        if ((type === 'rectangle' || type === 'oval') && shape.text && shape.text.trim()) {
          const lines = shape.text.split('\n');
          const totalHeight = lines.length * LINE_H;
          const startY = centerY - totalHeight / 2 + FONT_SIZE * 0.5;
          lines.forEach((line, i) => {
            const textEl = document.createElementNS(svgNS, 'text');
            textEl.setAttribute('x', String(centerX));
            textEl.setAttribute('y', String(startY + i * LINE_H));
            textEl.setAttribute('text-anchor', 'middle');
            textEl.setAttribute('dominant-baseline', 'middle');
            textEl.setAttribute('font-size', String(FONT_SIZE));
            textEl.setAttribute('font-family', 'Arial, sans-serif');
            textEl.setAttribute('fill', '#ffffff');
            textEl.textContent = line;
            shapeGroup.appendChild(textEl);
          });
        }

        // API block child box text
        if (type === 'apiBlock' && shape.children && shape.children.length > 0) {
          shape.children.forEach(childBox => {
            if (!childBox || !childBox.text || !childBox.position || !childBox.size) return;
            const textEl = document.createElementNS(svgNS, 'text');
            const textX = childBox.position.x + 65;
            const textY = childBox.position.y + childBox.size.height / 2;
            textEl.setAttribute('x', String(textX));
            textEl.setAttribute('y', String(textY));
            textEl.setAttribute('text-anchor', 'start');
            textEl.setAttribute('dominant-baseline', 'middle');
            textEl.setAttribute('font-size', '12');
            textEl.setAttribute('font-family', 'Arial, sans-serif');
            textEl.setAttribute('fill', '#ffffff');
            const maxChars = Math.max(5, Math.floor((childBox.size.width - 70) / 7));
            textEl.textContent = childBox.text.length > maxChars
              ? childBox.text.slice(0, maxChars - 1) + '\u2026'
              : childBox.text;
            shapeGroup.appendChild(textEl);
          });
        }

        // Table: render header + rows as native SVG rects/text
        if (type === 'table') {
          const cols = shape.columns || [];
          const rows = shape.rows || [];
          const HEADER_H = 32;
          const ROW_H = 30;
          const colW = (shape.size?.width || 300) / Math.max(1, cols.length);
          const svgNsEl = 'http://www.w3.org/2000/svg';
          cols.forEach((col, ci) => {
            const hRect = document.createElementNS(svgNsEl, 'rect');
            hRect.setAttribute('x', String(position.x + ci * colW));
            hRect.setAttribute('y', String(position.y));
            hRect.setAttribute('width', String(colW));
            hRect.setAttribute('height', String(HEADER_H));
            hRect.setAttribute('fill', 'rgba(59,130,246,0.25)');
            hRect.setAttribute('stroke', '#ffffff');
            hRect.setAttribute('stroke-width', '0.5');
            shapeGroup.appendChild(hRect);
            const hText = document.createElementNS(svgNsEl, 'text');
            hText.setAttribute('x', String(position.x + ci * colW + colW / 2));
            hText.setAttribute('y', String(position.y + HEADER_H / 2));
            hText.setAttribute('text-anchor', 'middle');
            hText.setAttribute('dominant-baseline', 'middle');
            hText.setAttribute('font-size', '12');
            hText.setAttribute('font-weight', 'bold');
            hText.setAttribute('font-family', 'Arial, sans-serif');
            hText.setAttribute('fill', '#ffffff');
            hText.textContent = col;
            shapeGroup.appendChild(hText);
          });
          rows.forEach((row, ri) => {
            cols.forEach((_, ci) => {
              const dRect = document.createElementNS(svgNsEl, 'rect');
              dRect.setAttribute('x', String(position.x + ci * colW));
              dRect.setAttribute('y', String(position.y + HEADER_H + ri * ROW_H));
              dRect.setAttribute('width', String(colW));
              dRect.setAttribute('height', String(ROW_H));
              dRect.setAttribute('fill', ri % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.05)');
              dRect.setAttribute('stroke', '#ffffff');
              dRect.setAttribute('stroke-width', '0.3');
              shapeGroup.appendChild(dRect);
              const cellText = (row[ci] || '').slice(0, Math.floor((colW - 12) / 7));
              if (cellText) {
                const dText = document.createElementNS(svgNsEl, 'text');
                dText.setAttribute('x', String(position.x + ci * colW + 6));
                dText.setAttribute('y', String(position.y + HEADER_H + ri * ROW_H + ROW_H / 2));
                dText.setAttribute('dominant-baseline', 'middle');
                dText.setAttribute('font-size', '12');
                dText.setAttribute('font-family', 'Arial, sans-serif');
                dText.setAttribute('fill', '#e2e8f0');
                dText.textContent = cellText;
                shapeGroup.appendChild(dText);
              }
            });
          });
        }

        // Text annotation: one <text> per explicit line (no auto-wrap)
        if (type === 'textAnnotation' && shape.text && shape.text.trim()) {
          const lines = shape.text.split('\n');
          const lineHeight = 20;
          lines.forEach((line, i) => {
            const textEl = document.createElementNS(svgNS, 'text');
            textEl.setAttribute('x', String(position.x));
            textEl.setAttribute('y', String(position.y + i * lineHeight + 14));
            textEl.setAttribute('font-size', '14');
            textEl.setAttribute('font-family', 'Arial, sans-serif');
            textEl.setAttribute('fill', '#e2e8f0');
            textEl.setAttribute('dominant-baseline', 'auto');
            textEl.textContent = line;
            shapeGroup.appendChild(textEl);
          });
        }
      });

      const svgStr = new XMLSerializer().serializeToString(clone);
      callback('data:image/svg+xml,' + encodeURIComponent(svgStr));
    } catch (e) {
      console.error('captureSnapshotAsync error:', e);
      callback(null);
    }
  };

  const handleSave = async () => {
    const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
    const drawing = {
      shapes,
      connections
    };
    
    setSaveLoading(true);
    try {
      const attemptKey = `hld-attempt-${problemId}`;
      const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
      
      // IMPORTANT: Only send the field that was updated (hldDiagram)
      // This is the update-drawing endpoint, NOT update-metadata
      // Other metadata (non-functional requirements, APIs, deep dives) use update-metadata endpoint
      await postRequest(
        process.env.REACT_APP_URL + '/hld-attempt/update-drawing',
        {
          userId: userId,
          hldProblemId: problemId,
          hldDiagram: drawing
        }
      );
      
      // Update localStorage to preserve all data and mark drawing as saved
      const updatedAttemptData = {
        ...attemptData, // Preserve all existing data
        drawing: drawing,
        timestamp: Date.now()
      };
      localStorage.setItem(attemptKey, JSON.stringify(updatedAttemptData));
      
      setSnackbar({ open: true, message: 'Drawing saved!', severity: 'success' });
      setHasUnsavedChanges(false); // Clear unsaved changes flag after successful save
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save drawing', severity: 'error' });
    } finally {
      setSaveLoading(false);
    }
  };

  // Removed duplicate useEffect hooks that were overriding localStorage data
  // The main fetchData function now handles all data loading properly

  // Fixed font size - text size remains constant
  const FIXED_FONT_SIZE = 14;
  
  // Helper function to calculate required shape size based on text
  // For rectangles: width stays constant, only height increases
  // For ovals: starts as circle (120x120), only height increases (becomes vertical oval)
  const calculateRequiredShapeSize = useCallback((type, text, currentSize) => {
    if (!text || text.length === 0) {
      // For ovals, return to circle size if no text
      if (type === 'oval') {
        const minSize = 120; // Minimum circle size
        return { width: minSize, height: minSize };
      }
      return currentSize;
    }
    
    const fontSize = FIXED_FONT_SIZE;
    const padding = type === 'oval' ? 30 : 16; // More padding for ovals (20px padding + 5px margin from boundary = 25px, rounded to 30px for safety)
    const lineHeightRatio = 1.2; // line height = fontSize * 1.2
    
    // Calculate text dimensions
    const lineHeight = fontSize * lineHeightRatio;
    
    // Split text into lines (respecting newlines and wrapping)
    const lines = text.split('\n');
    
    if (type === 'oval') {
      // For ovals, keep width constant (start as circle), only increase height
      // Calculate text wrapping within the fixed width (accounting for padding)
      const fixedWidth = 120; // Circle width
      const availableWidth = fixedWidth - padding; // Account for padding on both sides
      const avgCharWidth = fontSize * 0.6; // Approximate character width
      const maxCharsPerLine = Math.floor(availableWidth / avgCharWidth);
      
      // Calculate total lines needed (accounting for wrapping)
      let totalLines = 0;
      lines.forEach(line => {
        if (line.length === 0) {
          totalLines += 1; // Empty line still takes space
        } else {
          // Calculate how many wrapped lines this line needs
          const wrappedLines = Math.ceil(line.length / maxCharsPerLine);
          totalLines += Math.max(1, wrappedLines);
        }
      });
      
      // Calculate required height based on wrapped lines
      const requiredTextHeight = totalLines * lineHeight;
      const requiredHeight = Math.max(120, requiredTextHeight + padding); // Minimum circle size
      
      // Width stays constant at initial circle size (120)
      return {
        width: 120, // Keep width constant - always start as circle width
        height: requiredHeight // Only height increases to accommodate wrapped text
      };
    }
    
    // For rectangles, keep width constant, only increase height
    const availableWidth = currentSize.width - padding;
    const avgCharWidth = fontSize * 0.6;
    const maxCharsPerLine = Math.floor(availableWidth / avgCharWidth);
    
    // Calculate total lines needed (accounting for wrapping)
    let totalLines = 0;
    lines.forEach(line => {
      if (line.length === 0) {
        totalLines += 1;
      } else {
        const wrappedLines = Math.ceil(line.length / maxCharsPerLine);
        totalLines += Math.max(1, wrappedLines);
      }
    });
    
    const requiredTextHeight = totalLines * lineHeight;
    const minHeightWithPadding = padding;
    const requiredHeight = Math.max(minHeightWithPadding, requiredTextHeight + padding);
    
    return {
      width: currentSize.width,
      height: Math.max(currentSize.height, requiredHeight)
    };
  }, []);
  
  // Helper function to get font size (now fixed)
  const calculateOptimalFontSize = useCallback((shape, text) => {
    return FIXED_FONT_SIZE;
  }, []);

  // Handle text save - must be defined before renderChildBoxEditingOverlay
  const handleTextSave = useCallback(() => {
    saveHistory();
    // Handle child box text saving
    if (editingChildBox) {
      let cleanedText = editingText.replace(/\u200B/g, '').trim();
      
      setShapes(prev => prev.map(shape => {
        if (shape.id === editingChildBox.apiBlockId && shape.type === 'apiBlock' && shape.children) {
          return {
            ...shape,
            children: shape.children.map(childBox => {
              if (childBox.type === editingChildBox.boxType) {
                // Calculate new height based on text
                const lines = cleanedText.split('\n');
                const minHeight = 40;
                const lineHeight = 16;
                const padding = 10;
                const newHeight = Math.max(minHeight, (lines.length * lineHeight) + padding);
                
                return {
                  ...childBox,
                  text: cleanedText,
                  size: { ...childBox.size, height: newHeight }
                };
              }
              return childBox;
            })
          };
        }
        return shape;
      }));
      
      setHasUnsavedChanges(true);
      setEditingChildBox(null);
      setEditingText('');
      setEditingTextareaHeight(50);
      setSelectedTool("select");
      return;
    }
    
    // Handle regular shape text saving
    if (editingShape) {
      let cleanedText = editingText.replace(/\u200B/g, '').trim();

      const editedShape = shapesRef.current.find(s => s.id === editingShape);
      if (editedShape?.type === 'textAnnotation' && !cleanedText) {
        // Remove empty text annotations instead of keeping a blank one
        setShapes(prev => prev.filter(s => s.id !== editingShape));
      } else if (editedShape?.type === 'textAnnotation') {
        // Measure actual rendered width from the textarea before it unmounts
        const measuredW = textareaRef.current?.scrollWidth || editingTextareaWidth;
        const measuredH = textareaRef.current?.scrollHeight || editingTextareaHeight;
        setShapes(prev => prev.map(s =>
          s.id === editingShape
            ? { ...s, text: cleanedText, size: { width: measuredW, height: measuredH } }
            : s
        ));
      } else {
        // Shape is already resized dynamically as user types, so just update the text
        // Don't resize again to avoid any adjustments
        setShapes(prev => prev.map(s =>
          s.id === editingShape
            ? { ...s, text: cleanedText }
            : s
        ));
      }
      setHasUnsavedChanges(true);
      setEditingShape(null);
      setEditingText('');
      setEditingCenter({ x: 0, y: 0 }); // Clear editing center
      setEditingTextareaHeight(50); // Reset to default
      // Automatically switch to select tool after saving text
      setSelectedTool("select");
    }
  }, [editingShape, editingText, editingChildBox]);

  const handleTextCancel = useCallback(() => {
    setEditingShape(null);
    setEditingChildBox(null);
    setEditingText('');
    setEditingCenter({ x: 0, y: 0 }); // Clear editing center
    setEditingTextareaHeight(50); // Reset to default
    // Automatically switch to select tool after canceling text edit
    setSelectedTool("select");
  }, []);

  // Helper function to render child box editing overlay
  const renderChildBoxEditingOverlay = useCallback(() => {
    if (!editingChildBox) return null;
    
    const apiBlock = shapes.find(s => s.id === editingChildBox.apiBlockId && s.type === 'apiBlock');
    if (!apiBlock || !apiBlock.children) return null;
    
    const childBox = apiBlock.children.find(c => c.type === editingChildBox.boxType);
    if (!childBox) return null;
    
    const editingFontSize = 12;
    const textX = childBox.position.x + 60; // Start after label
    const textY = childBox.position.y + 5;
    const textWidth = childBox.size.width - 65;
    const textHeight = Math.max(childBox.size.height - 10, editingTextareaHeight);
    
    return (
      <foreignObject
        key={`child-edit-${editingChildBox.apiBlockId}-${editingChildBox.boxType}`}
        x={textX}
        y={textY}
        width={textWidth}
        height={textHeight}
        overflow="visible"
      >
        <textarea
          ref={textareaRef}
          value={editingText}
          onChange={(e) => {
            let newText = e.target.value;
            setEditingText(newText);
            
            // Auto-resize textarea
            e.target.style.height = 'auto';
            const scrollHeight = e.target.scrollHeight;
            const minHeight = editingFontSize * 1.2;
            const newHeight = Math.max(scrollHeight, minHeight);
            e.target.style.height = newHeight + 'px';
            setEditingTextareaHeight(newHeight);
            
            // Update child box height dynamically
            const lines = newText.split('\n');
            const lineHeight = 16;
            const padding = 10;
            const newBoxHeight = Math.max(40, (lines.length * lineHeight) + padding);
            
            setShapes(prev => prev.map(shape => {
              if (shape.id === editingChildBox.apiBlockId && shape.type === 'apiBlock' && shape.children) {
                return {
                  ...shape,
                  children: shape.children.map(cb => {
                    if (cb.type === editingChildBox.boxType) {
                      return {
                        ...cb,
                        size: { ...cb.size, height: newBoxHeight }
                      };
                    }
                    return cb;
                  })
                };
              }
              return shape;
            }));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              handleTextSave();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              handleTextCancel();
            }
          }}
          onBlur={handleTextSave}
          autoFocus
          spellCheck={false}
          style={{
            width: '100%',
            minHeight: `${editingFontSize * 1.2}px`,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            border: '2px solid #3b82f6',
            borderRadius: '4px',
            outline: 'none',
            color: '#ffffff',
            fontSize: `${editingFontSize}px`,
            padding: '4px',
            margin: '0',
            caretColor: '#ffffff',
            textAlign: 'left',
            fontFamily: 'Arial, sans-serif',
            lineHeight: `${editingFontSize * 1.2}px`,
            resize: 'none',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            boxSizing: 'border-box'
          }}
          wrap="soft"
        />
      </foreignObject>
    );
  }, [editingChildBox, shapes, editingText, editingTextareaHeight, handleTextSave, handleTextCancel, setEditingText, setEditingTextareaHeight, setShapes]);

  // Function to add child boxes to API block
  const addChildBoxesToApiBlock = useCallback((apiBlockId) => {
    setShapes((prev) => {
      return prev.map((shape) => {
        if (shape.id === apiBlockId && shape.type === 'apiBlock') {
          // Check if child boxes already exist
          if (shape.children && shape.children.length > 0) {
            return shape; // Don't add if already exists
          }
          
          const headerHeight = shape.headerHeight || 40;
          const padding = 10;
          const boxWidth = shape.size.width - (padding * 2);
          const boxHeight = 40;
          const boxSpacing = 5;
          const startY = shape.position.y + headerHeight + padding;
          
          const childBoxes = [
            { type: 'params', position: { x: shape.position.x + padding, y: startY }, size: { width: boxWidth, height: boxHeight }, text: '' },
            { type: 'body', position: { x: shape.position.x + padding, y: startY + boxHeight + boxSpacing }, size: { width: boxWidth, height: boxHeight }, text: '' },
            { type: 'header', position: { x: shape.position.x + padding, y: startY + (boxHeight + boxSpacing) * 2 }, size: { width: boxWidth, height: boxHeight }, text: '' },
          ];

          // Calculate new height to fit all boxes
          const totalChildHeight = (boxHeight * 3) + (boxSpacing * 2) + (padding * 2);
          const newHeight = headerHeight + totalChildHeight;
          
          return {
            ...shape,
            children: childBoxes,
            size: { ...shape.size, height: newHeight }
          };
        }
        return shape;
      });
    });
    setHasUnsavedChanges(true);
  }, []);

  // ── Table helpers ──────────────────────────────────────────────────────────
  const addTableRow = useCallback((tableId) => {
    saveHistory();
    setShapes(prev => prev.map(s => {
      if (s.id !== tableId || s.type !== 'table') return s;
      return { ...s, rows: [...s.rows, new Array(s.columns.length).fill('')] };
    }));
    setHasUnsavedChanges(true);
  }, [saveHistory]);

  const addTableCol = useCallback((tableId) => {
    saveHistory();
    setShapes(prev => prev.map(s => {
      if (s.id !== tableId || s.type !== 'table') return s;
      return {
        ...s,
        columns: [...s.columns, `Col ${s.columns.length + 1}`],
        rows: s.rows.map(row => [...row, '']),
      };
    }));
    setHasUnsavedChanges(true);
  }, [saveHistory]);

  const deleteTableRow = useCallback((tableId, rowIndex) => {
    saveHistory();
    setShapes(prev => prev.map(s => {
      if (s.id !== tableId || s.type !== 'table') return s;
      if (s.rows.length <= 1) return s; // keep at least one row
      return { ...s, rows: s.rows.filter((_, i) => i !== rowIndex) };
    }));
    setHasUnsavedChanges(true);
  }, [saveHistory]);

  const deleteTableCol = useCallback((tableId, colIndex) => {
    saveHistory();
    setShapes(prev => prev.map(s => {
      if (s.id !== tableId || s.type !== 'table') return s;
      if (s.columns.length <= 1) return s; // keep at least one column
      return {
        ...s,
        columns: s.columns.filter((_, i) => i !== colIndex),
        rows: s.rows.map(row => row.filter((_, i) => i !== colIndex)),
      };
    }));
    setHasUnsavedChanges(true);
  }, [saveHistory]);

  const insertTableColAt = useCallback((tableId, colIndex, side) => {
    saveHistory();
    setShapes(prev => prev.map(s => {
      if (s.id !== tableId || s.type !== 'table') return s;
      const at = side === 'after' ? colIndex + 1 : colIndex;
      const newCols = [...s.columns];
      newCols.splice(at, 0, `Col ${s.columns.length + 1}`);
      const newRows = s.rows.map(row => { const r = [...row]; r.splice(at, 0, ''); return r; });
      return { ...s, columns: newCols, rows: newRows };
    }));
    setHasUnsavedChanges(true);
  }, [saveHistory]);

  const insertTableRowAt = useCallback((tableId, rowIndex, side) => {
    saveHistory();
    setShapes(prev => prev.map(s => {
      if (s.id !== tableId || s.type !== 'table') return s;
      const at = side === 'after' ? rowIndex + 1 : rowIndex;
      const newRows = [...s.rows];
      newRows.splice(at, 0, new Array(s.columns.length).fill(''));
      return { ...s, rows: newRows };
    }));
    setHasUnsavedChanges(true);
  }, [saveHistory]);

  const saveEditCell = useCallback(() => {
    if (!editingCell) return;
    const { tableId, rowIndex, colIndex } = editingCell;
    const text = editingCellText;
    setShapes(prev => prev.map(s => {
      if (s.id !== tableId || s.type !== 'table') return s;
      if (rowIndex === -1) {
        const newCols = [...s.columns];
        newCols[colIndex] = text;
        return { ...s, columns: newCols };
      }
      const newRows = s.rows.map((row, ri) => {
        if (ri !== rowIndex) return row;
        const newRow = [...row];
        newRow[colIndex] = text;
        return newRow;
      });
      return { ...s, rows: newRows };
    }));
    setHasUnsavedChanges(true);
    setEditingCell(null);
    setEditingCellText('');
  }, [editingCell, editingCellText]);

  const renderShape = (shape) => {
    const { position, size, type, selected, componentType } = shape;
    const strokeColor = selected ? "#3b82f6" : "#ffffff";
    const strokeWidth = selected ? 2 : 1;

    // Render Text Annotation
    if (type === 'textAnnotation') {
      const LINE_HEIGHT = 20;
      const padH = 6;
      const padV = 4;
      const lines = (shape.text || '').split('\n');
      // Use browser-measured size stored in shape.size (set after first edit).
      // Fall back to character-width estimate only for brand-new unsaved annotations.
      const hasMeasuredSize = shape.size.width > 60 || (shape.text || '').length > 0;
      const fallbackW = Math.max(60, Math.max(1, ...lines.map(l => l.length)) * 7);
      const textW = hasMeasuredSize ? shape.size.width : fallbackW;
      const textH = shape.size.height || lines.length * LINE_HEIGHT;
      const isEditing = editingShape === shape.id;
      return (
        <g
          key={shape.id}
          data-shape-id={shape.id}
          onMouseDown={(e) => handleShapeMouseDown(e, shape.id)}
          onDoubleClick={(e) => { e.stopPropagation(); handleShapeDoubleClick(e, shape.id); }}
          style={{ cursor: 'move' }}
        >
          {/* Hit area + selection border — sized tightly to the actual text content */}
          <rect
            x={position.x - padH}
            y={position.y - padV}
            width={textW + padH * 2}
            height={textH + padV * 2}
            fill="transparent"
            stroke={(selected && !isEditing) ? "#3b82f6" : "none"}
            strokeWidth={(selected && !isEditing) ? 1.5 : 0}
            strokeDasharray={(selected && !isEditing) ? "4 3" : "none"}
            rx="3"
          />
          {/* Text rendered via foreignObject — pointer events disabled so the rect is the sole hit area */}
          {!isEditing && (
            <foreignObject
              x={position.x}
              y={position.y}
              width={textW}
              height={textH}
              overflow="visible"
              pointerEvents="none"
            >
              <div
                xmlns="http://www.w3.org/1999/xhtml"
                style={{
                  color: '#e2e8f0',
                  fontSize: '14px',
                  fontFamily: 'Arial, sans-serif',
                  lineHeight: '1.45',
                  whiteSpace: 'pre',
                  userSelect: 'none',
                }}
              >
                {shape.text || ''}
              </div>
            </foreignObject>
          )}
        </g>
      );
    }

    // Render Table
    if (type === 'table') {
      const cols = shape.columns || ['Column 1'];
      const rows = shape.rows || [[]];
      const HEADER_H = 32;
      // ROW_H derived from size.height so dragging a resize handle scales row heights
      const ROW_H = rows.length > 0
        ? Math.max(20, Math.round((size.height - HEADER_H) / rows.length))
        : 30;
      const colW = size.width / cols.length;
      const totalH = HEADER_H + rows.length * ROW_H;
      const btnStyle = {
        background: 'rgba(59,130,246,0.15)',
        border: '1px solid rgba(59,130,246,0.5)',
        color: '#93c5fd',
        fontSize: '11px',
        borderRadius: '4px',
        cursor: 'pointer',
        padding: '2px 8px',
        height: '22px',
      };
      return (
        <g
          key={shape.id}
          data-shape-id={shape.id}
          onMouseDown={(e) => handleShapeMouseDown(e, shape.id)}
        >
          {/* Outer border */}
          <rect
            x={position.x} y={position.y}
            width={size.width} height={totalH}
            fill="rgba(15,23,42,0.6)"
            stroke={strokeColor} strokeWidth={strokeWidth}
            rx="4"
          />
          {/* Header cells */}
          {cols.map((col, ci) => {
            const isColMenuOpen = tableMenu && tableMenu.tableId === shape.id && tableMenu.type === 'col' && tableMenu.index === ci;
            const triggerX = position.x + ci * colW + colW - 20;
            const triggerY = position.y + 6;
            return (
              <g key={`h${ci}`} onDoubleClick={(e) => {
                e.stopPropagation();
                setEditingCell({ tableId: shape.id, rowIndex: -1, colIndex: ci });
                setEditingCellText(col);
              }}>
                <rect
                  x={position.x + ci * colW} y={position.y}
                  width={colW} height={HEADER_H}
                  fill="rgba(59,130,246,0.25)"
                  stroke={strokeColor} strokeWidth={0.5}
                />
                {/* Column label */}
                <foreignObject x={position.x + ci * colW + 4} y={position.y} width={selected ? colW - 26 : colW - 8} height={HEADER_H} overflow="visible">
                  <div xmlns="http://www.w3.org/1999/xhtml" style={{
                    color: '#fff', fontSize: '12px', fontWeight: 'bold',
                    fontFamily: 'Arial, sans-serif', lineHeight: `${HEADER_H}px`,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    pointerEvents: 'none', userSelect: 'none',
                  }}>{col}</div>
                </foreignObject>
                {/* Column menu trigger (▾) */}
                {selected && (
                  <foreignObject x={triggerX} y={triggerY} width={18} height={20} style={{ overflow: 'visible' }}>
                    <div xmlns="http://www.w3.org/1999/xhtml">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setTableMenu(isColMenuOpen ? null : { tableId: shape.id, type: 'col', index: ci, x: triggerX, y: triggerY + 22 });
                        }}
                        style={{
                          width: '18px', height: '18px', background: isColMenuOpen ? 'rgba(99,102,241,0.35)' : 'rgba(255,255,255,0.12)',
                          border: '1px solid rgba(255,255,255,0.2)', borderRadius: '4px',
                          color: '#e2e8f0', fontSize: '9px', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                        }}
                      >▾</button>
                    </div>
                  </foreignObject>
                )}
                {/* Column dropdown menu */}
                {isColMenuOpen && (
                  <foreignObject x={triggerX - 80} y={triggerY + 22} width={140} height={120} style={{ overflow: 'visible', zIndex: 999 }}>
                    <div xmlns="http://www.w3.org/1999/xhtml">
                      <div style={{
                        background: '#1e293b', border: '1px solid #334155', borderRadius: '8px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.5)', overflow: 'hidden',
                        fontFamily: 'Arial, sans-serif', fontSize: '12px', width: '140px',
                      }}>
                        {[
                          { label: 'Insert left',  action: () => { insertTableColAt(shape.id, ci, 'before'); setTableMenu(null); } },
                          { label: 'Insert right', action: () => { insertTableColAt(shape.id, ci, 'after');  setTableMenu(null); } },
                          cols.length > 1 ? { label: 'Delete column', action: () => { deleteTableCol(shape.id, ci); setTableMenu(null); }, danger: true } : null,
                        ].filter(Boolean).map((item, idx, arr) => (
                          <div
                            key={item.label}
                            onClick={(e) => { e.stopPropagation(); item.action(); }}
                            style={{
                              padding: '8px 12px', cursor: 'pointer', color: item.danger ? '#f87171' : '#e2e8f0',
                              borderBottom: idx < arr.length - 1 ? '1px solid #334155' : 'none',
                              display: 'flex', alignItems: 'center', gap: '8px',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = item.danger ? 'rgba(239,68,68,0.15)' : '#334155'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <span style={{ fontSize: '11px' }}>
                              {item.label === 'Insert left' ? '⬅' : item.label === 'Insert right' ? '➡' : '🗑'}
                            </span>
                            {item.label}
                          </div>
                        ))}
                      </div>
                    </div>
                  </foreignObject>
                )}
              </g>
            );
          })}
          {/* Vertical dividers for header */}
          {cols.map((_, ci) => ci > 0 && (
            <line key={`vd${ci}`}
              x1={position.x + ci * colW} y1={position.y}
              x2={position.x + ci * colW} y2={position.y + totalH}
              stroke={strokeColor} strokeWidth={0.5} strokeOpacity={0.4}
            />
          ))}
          {/* Horizontal divider below header */}
          <line
            x1={position.x} y1={position.y + HEADER_H}
            x2={position.x + size.width} y2={position.y + HEADER_H}
            stroke={strokeColor} strokeWidth={0.5}
          />
          {/* Data rows */}
          {rows.map((row, ri) => {
            const isRowMenuOpen = tableMenu && tableMenu.tableId === shape.id && tableMenu.type === 'row' && tableMenu.index === ri;
            const rowTriggerX = position.x + size.width - 20;
            const rowTriggerY = position.y + HEADER_H + ri * ROW_H + (ROW_H - 18) / 2;
            return (
              <g key={`r${ri}`}>
                <line
                  x1={position.x} y1={position.y + HEADER_H + ri * ROW_H}
                  x2={position.x + size.width} y2={position.y + HEADER_H + ri * ROW_H}
                  stroke={strokeColor} strokeWidth={0.5} strokeOpacity={0.2}
                />
                {cols.map((_, ci) => (
                  <g key={`r${ri}c${ci}`} onDoubleClick={(e) => {
                    e.stopPropagation();
                    setEditingCell({ tableId: shape.id, rowIndex: ri, colIndex: ci });
                    setEditingCellText((row[ci]) || '');
                  }}>
                    <rect
                      x={position.x + ci * colW}
                      y={position.y + HEADER_H + ri * ROW_H}
                      width={colW} height={ROW_H}
                      fill={ri % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.05)'}
                      stroke="transparent"
                    />
                    <foreignObject
                      x={position.x + ci * colW + 6}
                      y={position.y + HEADER_H + ri * ROW_H}
                      width={ci === cols.length - 1 && selected ? colW - 28 : colW - 12} height={ROW_H}
                      overflow="visible"
                    >
                      <div xmlns="http://www.w3.org/1999/xhtml" style={{
                        color: '#e2e8f0', fontSize: '12px',
                        fontFamily: 'Arial, sans-serif', lineHeight: `${ROW_H}px`,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        pointerEvents: 'none', userSelect: 'none',
                      }}>{row[ci] || ''}</div>
                    </foreignObject>
                  </g>
                ))}
                {/* Row menu trigger (▾) — inside the last cell, top-right */}
                {selected && (
                  <foreignObject x={rowTriggerX} y={rowTriggerY} width={18} height={18} style={{ overflow: 'visible' }}>
                    <div xmlns="http://www.w3.org/1999/xhtml">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setTableMenu(isRowMenuOpen ? null : { tableId: shape.id, type: 'row', index: ri, x: rowTriggerX, y: rowTriggerY + 20 });
                        }}
                        style={{
                          width: '18px', height: '18px', background: isRowMenuOpen ? 'rgba(99,102,241,0.35)' : 'rgba(255,255,255,0.1)',
                          border: '1px solid rgba(255,255,255,0.18)', borderRadius: '4px',
                          color: '#e2e8f0', fontSize: '9px', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                        }}
                      >▾</button>
                    </div>
                  </foreignObject>
                )}
                {/* Row dropdown menu */}
                {isRowMenuOpen && (
                  <foreignObject x={rowTriggerX - 100} y={rowTriggerY + 20} width={140} height={120} style={{ overflow: 'visible', zIndex: 999 }}>
                    <div xmlns="http://www.w3.org/1999/xhtml">
                      <div style={{
                        background: '#1e293b', border: '1px solid #334155', borderRadius: '8px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.5)', overflow: 'hidden',
                        fontFamily: 'Arial, sans-serif', fontSize: '12px', width: '140px',
                      }}>
                        {[
                          { label: 'Insert above', action: () => { insertTableRowAt(shape.id, ri, 'before'); setTableMenu(null); } },
                          { label: 'Insert below', action: () => { insertTableRowAt(shape.id, ri, 'after');  setTableMenu(null); } },
                          rows.length > 1 ? { label: 'Delete row', action: () => { deleteTableRow(shape.id, ri); setTableMenu(null); }, danger: true } : null,
                        ].filter(Boolean).map((item, idx, arr) => (
                          <div
                            key={item.label}
                            onClick={(e) => { e.stopPropagation(); item.action(); }}
                            style={{
                              padding: '8px 12px', cursor: 'pointer', color: item.danger ? '#f87171' : '#e2e8f0',
                              borderBottom: idx < arr.length - 1 ? '1px solid #334155' : 'none',
                              display: 'flex', alignItems: 'center', gap: '8px',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = item.danger ? 'rgba(239,68,68,0.15)' : '#334155'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <span style={{ fontSize: '11px' }}>
                              {item.label === 'Insert above' ? '⬆' : item.label === 'Insert below' ? '⬇' : '🗑'}
                            </span>
                            {item.label}
                          </div>
                        ))}
                      </div>
                    </div>
                  </foreignObject>
                )}
              </g>
            );
          })}
          {/* Add Row / Add Column buttons (visible when selected) */}
          {selected && (
            <>
              <foreignObject x={position.x} y={position.y + totalH + 6} width={70} height={24}>
                <div xmlns="http://www.w3.org/1999/xhtml">
                  <button style={btnStyle} onClick={(e) => { e.stopPropagation(); addTableRow(shape.id); }}>+ Row</button>
                </div>
              </foreignObject>
              <foreignObject x={position.x + 76} y={position.y + totalH + 6} width={80} height={24}>
                <div xmlns="http://www.w3.org/1999/xhtml">
                  <button style={btnStyle} onClick={(e) => { e.stopPropagation(); addTableCol(shape.id); }}>+ Column</button>
                </div>
              </foreignObject>
            </>
          )}
        </g>
      );
    }

    // Render API Block
    if (type === "apiBlock") {
      const headerHeight = shape.headerHeight || 40;
      const padding = 10;
      
      return (
        <g 
          key={shape.id} 
          data-shape-id={shape.id}
          onMouseDown={(e) => {
            // Don't select API block if clicking on child box or header button
            if (e.target.closest('.api-child-box') || e.target.closest('.api-add-button')) {
              return;
            }
            handleShapeMouseDown(e, shape.id);
          }}
        >
          {/* Main API Block Container */}
          <rect
            x={position.x}
            y={position.y}
            width={size.width}
            height={size.height}
            fill="rgba(59, 130, 246, 0.1)"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            rx="4"
            ry="4"
            className="cursor-pointer"
          />
          
          {/* Header */}
          <rect
            x={position.x}
            y={position.y}
            width={size.width}
            height={headerHeight}
            fill="rgba(59, 130, 246, 0.2)"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            rx="4"
            ry="4"
          />
          
          {/* Header Text */}
          <text
            x={position.x + padding}
            y={position.y + headerHeight / 2}
            fill="#ffffff"
            fontSize="14"
            fontWeight="bold"
            dominantBaseline="middle"
          >
            API Block
          </text>
          
          {/* Add Child Boxes Button */}
          {(!shape.children || shape.children.length === 0) && (
            <foreignObject
              x={position.x + size.width - 80}
              y={position.y + 5}
              width={75}
              height={30}
            >
              <button
                className="api-add-button"
                onClick={(e) => {
                  e.stopPropagation();
                  addChildBoxesToApiBlock(shape.id);
                }}
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px'
                }}
              >
                <FaPlus style={{ fontSize: '10px' }} />
                Add Boxes
              </button>
            </foreignObject>
          )}
          
          {/* Render Child Boxes */}
          {shape.children && shape.children.map((childBox, index) => {
            const boxType = childBox.type;
            const boxLabel = boxType === 'header' ? 'Headers' : boxType.charAt(0).toUpperCase() + boxType.slice(1);
            const isEditing = editingChildBox && editingChildBox.apiBlockId === shape.id && editingChildBox.boxType === boxType;
            const accentColor =
              boxType === 'params' ? '#38bdf8' :
              boxType === 'body'   ? '#34d399' :
              boxType === 'header' ? '#fbbf24' : '#94a3b8';
            const boxFill =
              boxType === 'params' ? 'rgba(56,189,248,0.12)' :
              boxType === 'body'   ? 'rgba(52,211,153,0.12)' :
              boxType === 'header' ? 'rgba(251,191,36,0.12)' : 'rgba(255,255,255,0.08)';

            return (
              <g key={`${shape.id}-${boxType}`} className="api-child-box">
                {/* Child Box Background */}
                <rect
                  x={childBox.position.x}
                  y={childBox.position.y}
                  width={childBox.size.width}
                  height={childBox.size.height}
                  fill={boxFill}
                  stroke={accentColor}
                  strokeWidth={1}
                  rx="3"
                  ry="3"
                  className="cursor-pointer"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    if (selectedTool === "select") {
                      const rect = svgRef.current?.getBoundingClientRect();
                      if (!rect) return;
                      setDraggedChildBox({ apiBlockId: shape.id, boxType: boxType });
                      setChildBoxDragOffset({
                        x: e.clientX - rect.left - childBox.position.x,
                        y: e.clientY - rect.top - childBox.position.y,
                      });
                    }
                  }}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    if (selectedTool === "select") {
                      setEditingChildBox({ apiBlockId: shape.id, boxType: boxType });
                      setEditingText(childBox.text || '');
                      if (childBox.text) {
                        const lines = childBox.text.split('\n');
                        const estimatedHeight = Math.max(40, (lines.length * 16) + 10);
                        setEditingTextareaHeight(estimatedHeight);
                      } else {
                        setEditingTextareaHeight(40);
                      }
                    }
                  }}
                />

                {/* Colored left accent bar */}
                <rect
                  x={childBox.position.x}
                  y={childBox.position.y}
                  width={3}
                  height={childBox.size.height}
                  fill={accentColor}
                  rx="3"
                  ry="3"
                  style={{ pointerEvents: 'none' }}
                />

                {/* Child Box Label */}
                <text
                  x={childBox.position.x + 9}
                  y={childBox.position.y + 14}
                  fill={accentColor}
                  fontSize="9"
                  fontWeight="700"
                  dominantBaseline="middle"
                  style={{ textTransform: 'uppercase', letterSpacing: '0.5px', pointerEvents: 'none' }}
                >
                  {boxLabel.toUpperCase()}
                </text>

                {/* Child Box Text Content */}
                {!isEditing && childBox.text && (
                  <foreignObject
                    x={childBox.position.x + 9}
                    y={childBox.position.y + 22}
                    width={childBox.size.width - 14}
                    height={childBox.size.height - 26}
                  >
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        color: 'rgba(255,255,255,0.85)',
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'pre-wrap',
                        lineHeight: '1.4'
                      }}
                    >
                      {childBox.text}
                    </div>
                  </foreignObject>
                )}
              </g>
            );
          })}
        </g>
      );
    }

    if (type === "component" && componentType) {
      const component = HLD_COMPONENTS[componentType];
      if (!component) return null;

      const IconComponent = component.icon;
      const centerX = position.x + size.width / 2;
      const centerY = position.y + size.height / 2;

      return (
        <g 
          key={shape.id} 
          data-shape-id={shape.id}
          onMouseDown={(e) => handleShapeMouseDown(e, shape.id)}
          onDoubleClick={(e) => handleShapeDoubleClick(e, shape.id)}
        >
          {/* Background rectangle */}
          <rect
            x={position.x}
            y={position.y}
            width={size.width}
            height={size.height}
            fill="rgba(0, 0, 0, 0.1)"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            rx="8"
            ry="8"
            className="cursor-pointer"
          />
          
          {/* Icon */}
          <foreignObject
            x={position.x + size.width * 0.1}
            y={position.y + size.height * 0.1}
            width={size.width * 0.8}
            height={size.height * 0.8}
          >
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: component.color,
              fontSize: `${Math.max(12, Math.min(48, size.width * 0.2))}px`
            }}>
              <IconComponent />
            </div>
          </foreignObject>
          
          {/* Component name */}
          <text
            x={centerX}
            y={position.y + size.height + Math.max(10, size.height * 0.15)}
            textAnchor="middle"
            fontSize={Math.max(8, Math.min(16, size.width * 0.08))}
            fill="#ffffff"
            className="cursor-pointer"
          >
            {componentType}
          </text>
        </g>
      );
    }

    // Fallback for old shape types
    switch (type) {
      case "rectangle":
        const rectCenterX = position.x + size.width / 2;
        const rectCenterY = position.y + size.height / 2;
        const rectFontSize = FIXED_FONT_SIZE;
        return (
          <g 
            key={shape.id} 
            data-shape-id={shape.id}
            onMouseDown={(e) => handleShapeMouseDown(e, shape.id)}
            onDoubleClick={(e) => {
              e.stopPropagation();
              handleShapeDoubleClick(e, shape.id);
              // Also handle text editing on double click
              const shapeData = shapes.find(s => s.id === shape.id);
              // Store the center point for symmetric growth
              const centerX = position.x + size.width / 2;
              const centerY = position.y + size.height / 2;
              setEditingCenter({ x: centerX, y: centerY });
              setEditingShape(shape.id);
              setEditingText(shapeData?.text || '');
              // Set editing position to center of rectangle
              setEditingPosition({ x: centerX, y: centerY });
              // Initialize textarea height based on text content
              if (shapeData?.text) {
                const lines = shapeData.text.split('\n');
                const lineCount = lines.length;
                const estimatedHeight = Math.max(16, lineCount * FIXED_FONT_SIZE * 1.2);
                setEditingTextareaHeight(estimatedHeight);
              } else {
                setEditingTextareaHeight(16);
              }
            }}
          >
            <rect
              x={position.x}
              y={position.y}
              width={size.width}
              height={size.height}
              fill="rgba(59, 130, 246, 0.1)"
              stroke={strokeColor}
              strokeWidth={strokeWidth}
              className="cursor-pointer"
            />
            {shape.text && editingShape !== shape.id && (
              <foreignObject
                x={position.x}
                y={position.y}
                width={size.width}
                height={size.height}
              >
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    justifyContent: 'center',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    paddingLeft: '8px',
                    paddingRight: '8px',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    minHeight: '16px'
                  }}
                className="cursor-pointer"
                >
                  <div
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      color: '#ffffff',
                      fontSize: `${rectFontSize}px`,
                      fontFamily: 'Arial, sans-serif',
                      textAlign: 'center',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'pre-wrap',
                      lineHeight: `${rectFontSize * 1.2}px`,
                      overflow: 'hidden',
                      display: 'block',
                      margin: 'auto 0',
                      padding: '0',
                      boxSizing: 'border-box'
                    }}
              >
                {shape.text}
                  </div>
                </div>
              </foreignObject>
            )}
          </g>
        );
      case "oval":
        const ovalCenterX = position.x + size.width / 2;
        const ovalCenterY = position.y + size.height / 2;
        const ovalRadiusX = size.width / 2;
        const ovalRadiusY = size.height / 2;
        const ovalFontSize = FIXED_FONT_SIZE;
        return (
          <g 
            key={shape.id} 
            data-shape-id={shape.id}
            onMouseDown={(e) => handleShapeMouseDown(e, shape.id)}
            onDoubleClick={(e) => {
              e.stopPropagation();
              handleShapeDoubleClick(e, shape.id);
              // Also handle text editing on double click
              const shapeData = shapes.find(s => s.id === shape.id);
              // Store the center point for symmetric growth
              const centerX = position.x + size.width / 2;
              const centerY = position.y + size.height / 2;
              setEditingCenter({ x: centerX, y: centerY });
              setEditingShape(shape.id);
              setEditingText(shapeData?.text || '');
              // Set editing position to center of oval
              setEditingPosition({ x: centerX, y: centerY });
              // Initialize textarea height based on text content
              if (shapeData?.text) {
                const lines = shapeData.text.split('\n');
                const lineCount = lines.length;
                const estimatedHeight = Math.max(16, lineCount * FIXED_FONT_SIZE * 1.2);
                setEditingTextareaHeight(estimatedHeight);
              } else {
                setEditingTextareaHeight(16);
              }
            }}
          >
            {/* Clip path definition for this oval - accounts for padding margin (at least 5px from boundary) */}
            <defs>
              <clipPath id={`oval-clip-${shape.id}`}>
                <ellipse
                  cx={ovalCenterX}
                  cy={ovalCenterY}
                  rx={ovalRadiusX - 20}
                  ry={ovalRadiusY - 20}
                />
              </clipPath>
            </defs>
            <ellipse
              cx={ovalCenterX}
              cy={ovalCenterY}
              rx={ovalRadiusX}
              ry={ovalRadiusY}
              fill="rgba(59, 130, 246, 0.1)"
              stroke={strokeColor}
              strokeWidth={strokeWidth}
              className="cursor-pointer"
            />
            {shape.text && editingShape !== shape.id && (
              <foreignObject
                x={position.x}
                y={position.y}
                width={size.width}
                height={size.height}
              >
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    justifyContent: 'center',
                    paddingTop: '20px',
                    paddingBottom: '20px',
                    paddingLeft: '20px',
                    paddingRight: '20px',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    minHeight: '16px'
                  }}
                className="cursor-pointer"
                >
                  <div
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      color: '#ffffff',
                      fontSize: `${ovalFontSize}px`,
                      fontFamily: 'Arial, sans-serif',
                      textAlign: 'center',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'pre-wrap',
                      lineHeight: `${ovalFontSize * 1.2}px`,
                      overflow: 'hidden',
                      display: 'block',
                      margin: 'auto 0',
                      padding: '0',
                      boxSizing: 'border-box'
                    }}
              >
                {shape.text}
                  </div>
                </div>
              </foreignObject>
            )}
          </g>
        );
      case "diamond":
        const centerX = position.x + size.width / 2;
        const centerY = position.y + size.height / 2;
        const points = `${centerX},${position.y} ${position.x + size.width},${centerY} ${centerX},${position.y + size.height} ${position.x},${centerY}`;
        return (
          <g 
            key={shape.id}
            onMouseDown={(e) => handleShapeMouseDown(e, shape.id)}
            onDoubleClick={(e) => handleShapeDoubleClick(e, shape.id)}
          >
            <polygon
              points={points}
              fill="transparent"
              stroke={strokeColor}
              strokeWidth={strokeWidth}
              className="cursor-pointer"
            />
          </g>
        );
      default:
        return null;
    }
  };

  const handleDoubleClick = useCallback((e, shapeId) => {
    e.stopPropagation();
    const shape = shapes.find(s => s.id === shapeId);
    if (shape) {
      setEditingShape(shapeId);
      const initialText = shape.text || '';
      setEditingText(initialText);
      // Reset textarea height when starting to edit
      const lineCount = initialText.split('\n').length || 1;
      setTextareaHeight(lineCount * 16);
      // Initialize container width - will be measured by useEffect
      // Set initial value, useEffect will measure and update it properly
      if (initialText) {
        const longestLine = initialText.split('\n').reduce((longest, line) => 
          line.length > longest.length ? line : longest, '');
        // Approximate initial width, useEffect will measure accurately
        const initialWidth = Math.max(16, longestLine.length * 8 + 8);
        setContainerWidth(initialWidth);
      } else {
        setContainerWidth(16); // Start with approximate one character width
      }
      setEditingPosition({
        x: shape.position.x + shape.size.width / 2,
        y: shape.position.y + shape.size.height / 2
      });
    }
  }, [shapes]);

  // Function to resize shape based on text (can be called during editing)
  // Width stays constant, only height increases (grows downward from center)
  const resizeShapeForText = useCallback((shapeId, text) => {
    setShapes(prev => {
      const currentShape = prev.find(s => s.id === shapeId);
      if (!currentShape) return prev;
      
      // Calculate required size based on text (width stays same, height increases)
      const requiredSize = calculateRequiredShapeSize(
        currentShape.type,
        text,
        currentShape.size
      );
      
      // Use the stored editing center - width stays constant, only height grows
      // If editingCenter is not set, use current center as fallback
      const centerX = editingCenter.x || (currentShape.position.x + currentShape.size.width / 2);
      const centerY = editingCenter.y || (currentShape.position.y + currentShape.size.height / 2);
      
      // Position: X stays centered on editingCenter.x
      // Y grows symmetrically from center (both up and down)
      const newPosition = {
        x: centerX - requiredSize.width / 2, // Center horizontally (width stays same)
        y: centerY - requiredSize.height / 2 // Center vertically, grow symmetrically
      };
      
      return prev.map(shape => {
        if (shape.id === shapeId) {
          return {
            ...shape,
            size: requiredSize,
            position: newPosition
          };
        }
        return shape;
      });
    });
    setHasUnsavedChanges(true);
  }, [calculateRequiredShapeSize, editingCenter]);

  // handleTextSave and handleTextCancel moved above renderChildBoxEditingOverlay to fix initialization order

  const renderResizeHandles = (shape) => {
    if (!shape.selected) return null;
    if (shape.type === 'textAnnotation') return null; // no resize handles for free text

    const { position, size } = shape;
    const handleSize = 6;

    // For API blocks, only show bottom edge resize handle (height-only resizing)
    if (shape.type === "apiBlock") {
      const handleX = position.x + size.width / 2 - handleSize / 2;
      const handleY = position.y + size.height - handleSize / 2;
      return (
        <rect
          key={`${shape.id}-bottom`}
          x={handleX}
          y={handleY}
          width={handleSize * 2}
          height={handleSize}
          fill="#3b82f6"
          stroke="#ffffff"
          strokeWidth={1}
          className="cursor-ns-resize"
          onMouseDown={(e) => handleResizeMouseDown(e, shape.id, "bottom")}
        />
      );
    }

    // For circles, we still use corner handles but adjust visually
    const handles = [
      { id: "nw", x: position.x - handleSize / 2, y: position.y - handleSize / 2 },
      { id: "ne", x: position.x + size.width - handleSize / 2, y: position.y - handleSize / 2 },
      { id: "sw", x: position.x - handleSize / 2, y: position.y + size.height - handleSize / 2 },
      { id: "se", x: position.x + size.width - handleSize / 2, y: position.y + size.height - handleSize / 2 },
    ];

    return handles.map((handle) => (
      <rect
        key={`${shape.id}-${handle.id}`}
        x={handle.x}
        y={handle.y}
        width={handleSize}
        height={handleSize}
        fill="#3b82f6"
        stroke="#ffffff"
        strokeWidth={1}
        className="cursor-pointer"
        onMouseDown={(e) => handleResizeMouseDown(e, shape.id, handle.id)}
      />
    ));
  };

  const getShapeBorderPoint = (shape, fromPoint) => {
    const center = getShapeCenter(shape);
    const dx = fromPoint.x - center.x;
    const dy = fromPoint.y - center.y;
    if (dx === 0 && dy === 0) return center;

    // For component type shapes, use rectangle border calculation
    if (shape.type === "component") {
      const halfWidth = shape.size.width / 2;
      const halfHeight = shape.size.height / 2;
      const slope = dy / dx;
      let borderX, borderY;

      if (Math.abs(dx) / halfWidth > Math.abs(dy) / halfHeight) {
        borderX = center.x + (dx > 0 ? halfWidth : -halfWidth);
        borderY = center.y + slope * (borderX - center.x);
      } else {
        borderY = center.y + (dy > 0 ? halfHeight : -halfHeight);
        borderX = center.x + (borderY - center.y) / slope;
      }
      return { x: borderX, y: borderY };
    }

    switch (shape.type) {
      case "rectangle": {
        const halfWidth = shape.size.width / 2;
        const halfHeight = shape.size.height / 2;
        const slope = dy / dx;
        let borderX, borderY;

        if (Math.abs(dx) / halfWidth > Math.abs(dy) / halfHeight) {
          borderX = center.x + (dx > 0 ? halfWidth : -halfWidth);
          borderY = center.y + slope * (borderX - center.x);
        } else {
          borderY = center.y + (dy > 0 ? halfHeight : -halfHeight);
          borderX = center.x + (borderY - center.y) / slope;
        }
        return { x: borderX, y: borderY };
      }
      case "oval": {
        const radiusX = shape.size.width / 2;
        const radiusY = shape.size.height / 2;
        
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance === 0) return center;
        
        // Normalize direction
        const normalizedDx = dx / distance;
        const normalizedDy = dy / distance;
        
        // Calculate angle
        const angle = Math.atan2(dy, dx);
        
        // Calculate point on ellipse border
        const borderX = center.x + radiusX * Math.cos(angle);
        const borderY = center.y + radiusY * Math.sin(angle);
        
        return { x: borderX, y: borderY };
      }
      case "diamond": {
        const halfWidth = shape.size.width / 2;
        const halfHeight = shape.size.height / 2;

        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance === 0) return center;

        const normalizedDx = dx / distance;
        const normalizedDy = dy / distance;

        const t1 = halfWidth / Math.abs(normalizedDx + normalizedDy);
        const t2 = halfWidth / Math.abs(normalizedDx - normalizedDy);

        const t = Math.min(t1, t2);

        return {
          x: center.x + normalizedDx * t,
          y: center.y + normalizedDy * t,
        };
      }
      default:
        return center;
    }
  };

  const renderConnection = (connection) => {
    const fromShape = shapes.find((s) => s.id === connection.from);
    const toShape = shapes.find((s) => s.id === connection.to);
    if (!fromShape || !toShape) return null;

    const fromCenter = getShapeCenter(fromShape);
    const toCenter = getShapeCenter(toShape);

    const fromBorder = getShapeBorderPoint(fromShape, toCenter);
    const toBorder = getShapeBorderPoint(toShape, fromCenter);

    const isSelected = selectedConnection === connection.id;

    return (
      <g key={connection.id} onMouseDown={(e) => handleConnectionClick(e, connection.id)}>
        {/* Invisible thick line for easier clicking */}
      <line
        x1={fromBorder.x}
        y1={fromBorder.y}
        x2={toBorder.x}
        y2={toBorder.y}
          stroke="transparent"
          strokeWidth={12}
          className="cursor-pointer"
        />
        {/* Visible line */}
        <line
          x1={fromBorder.x}
          y1={fromBorder.y}
          x2={toBorder.x}
          y2={toBorder.y}
          stroke={isSelected ? "#3b82f6" : "#ffffff"}
          strokeWidth={isSelected ? 2 : 1}
        markerEnd="url(#arrowhead)"
          className="cursor-pointer"
        />
      </g>
    );
  };

  if(loading) return <div style={{ marginTop: '100px' }}><Loader /></div>;
  // Only show "Problem not found" if loading is complete and problem is still null
  if(!loading && !problem) return <div style={{textAlign:'center',marginTop:'30px'}}>Problem not found</div>;

  return (
      <div style={{
      height: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
      overflow: 'hidden'
      }}>
        {/* Toolbar */}
        <div style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: '#f7f7f7',
          padding: 8,
          borderRadius: 4,
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 4,
          justifyContent: 'center',
          margin: '5px',
          border: '1px solid #ddd'
        }}>
          <button
            onClick={() => {
              setSelectedTool("select");
              setConnectionStart(null);
              setSelectedComponent(null);
              setEditingShape(null);
            }}
            style={{
              backgroundColor: selectedTool === "select" && !selectedComponent ? '#007bff' : 'white',
              color: selectedTool === "select" && !selectedComponent ? 'white' : 'black',
              border: selectedTool === "select" && !selectedComponent ? '2px solid #007bff' : '1px solid #ccc',
              padding: '4px 8px',
              borderRadius: 4,
              height: '32px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Select"
          >
            <FaMousePointer style={{ fontSize: '12px' }} />
            <span style={{ fontSize: '11px', marginLeft: '4px' }}>Select</span>
          </button>

          <button
            onClick={() => { setSelectedTool("rectangle"); setSelectedComponent(null); setConnectionStart(null); }}
            style={{
              backgroundColor: selectedTool === "rectangle" && !selectedComponent ? '#007bff' : 'white',
              color: selectedTool === "rectangle" && !selectedComponent ? 'white' : 'black',
              border: selectedTool === "rectangle" && !selectedComponent ? '2px solid #007bff' : '1px solid #ccc',
              padding: '4px 8px',
              borderRadius: 4,
              height: '32px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Box"
          >
            <FaVectorSquare style={{ fontSize: '12px' }} />
            <span style={{ fontSize: '11px', marginLeft: '4px' }}>Box</span>
          </button>

          <button
            onClick={() => { setSelectedTool("oval"); setSelectedComponent(null); setConnectionStart(null); }}
            style={{
              backgroundColor: selectedTool === "oval" && !selectedComponent ? '#007bff' : 'white',
              color: selectedTool === "oval" && !selectedComponent ? 'white' : 'black',
              border: selectedTool === "oval" && !selectedComponent ? '2px solid #007bff' : '1px solid #ccc',
              padding: '4px 8px',
              borderRadius: 4,
              height: '32px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Circle"
          >
            <FaRegCircle style={{ fontSize: '12px' }} />
            <span style={{ fontSize: '11px', marginLeft: '4px' }}>Circle</span>
          </button>

          {/* Component Dropdown */}
          <div style={{ position: 'relative', marginLeft: 4 }} className="component-dropdown">
          <button
              onClick={() => {
                setShowComponentDropdown(!showComponentDropdown);
                if (!showComponentDropdown) {
                  // When opening dropdown, clear component selection
                  setSelectedComponent(null);
                }
              }}
            style={{
                backgroundColor: selectedComponent ? '#007bff' : 'white',
                color: selectedComponent ? 'white' : 'black',
                border: selectedComponent ? '2px solid #007bff' : '1px solid #ccc',
              padding: '4px 8px',
              borderRadius: 4,
              height: '32px',
              display: 'inline-flex',
              alignItems: 'center',
                justifyContent: 'center',
                minWidth: '120px'
            }}
              title="Add Component"
          >
              {selectedComponent ? selectedComponent : 'Add Component'}
          </button>

            {showComponentDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                maxHeight: '400px',
                overflowY: 'auto',
                zIndex: 1000,
                minWidth: '250px'
              }}>
                {/* Search Input */}
                <div style={{
                  padding: '8px 12px',
                  position: 'sticky',
                  top: 0,
                  backgroundColor: 'white',
                  zIndex: 1
                }}>
                  <input
                    type="text"
                    placeholder="Search components..."
                    value={componentSearchTerm}
                    onChange={(e) => setComponentSearchTerm(e.target.value)}
            style={{
                      width: '100%',
                      padding: '6px 8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '12px',
                      outline: 'none'
                    }}
                    autoFocus
                  />
                </div>

                {/* Component List */}
                {Object.entries(HLD_COMPONENTS)
                  .filter(([name, component]) =>
                    name.toLowerCase().includes(componentSearchTerm.toLowerCase()) ||
                    component.category.toLowerCase().includes(componentSearchTerm.toLowerCase())
                  )
                  .map(([name, component]) => {
                    const IconComponent = component.icon;
                    return (
                      <div
                        key={name}
            onClick={() => {
                          setSelectedComponent(name);
                          setShowComponentDropdown(false);
                          setSelectedTool("component"); // Use a different tool state for component placement
                          setComponentSearchTerm(''); // Clear search when selecting
            }}
            style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          display: 'flex',
              alignItems: 'center',
                          gap: '8px',
                          fontSize: '12px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                      >
                        <IconComponent style={{ color: component.color, fontSize: '14px' }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '500' }}>{name}</div>
                          <div style={{ fontSize: '10px', color: '#666' }}>{component.category}</div>
                        </div>
                      </div>
                    );
                  })}

                {/* No Results Message */}
                {Object.entries(HLD_COMPONENTS).filter(([name, component]) =>
                  name.toLowerCase().includes(componentSearchTerm.toLowerCase()) ||
                  component.category.toLowerCase().includes(componentSearchTerm.toLowerCase())
                ).length === 0 && componentSearchTerm && (
                  <div style={{
                    padding: '12px',
                    textAlign: 'center',
                    color: '#666',
                    fontSize: '12px'
                  }}>
                    No components found matching "{componentSearchTerm}"
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            onClick={() => {
              setSelectedTool("line");
              setSelectedComponent(null);
            }}
            style={{
              marginLeft: 4,
              backgroundColor: selectedTool === "line" ? '#007bff' : 'white',
              color: selectedTool === "line" ? 'white' : 'black',
              border: selectedTool === "line" ? '2px solid #007bff' : '1px solid #ccc',
              padding: '4px 8px',
              borderRadius: 4,
              height: '32px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Connect"
          >
            <FaMinus style={{ fontSize: '12px' }} />
            <span style={{ fontSize: '11px', marginLeft: '4px' }}>Connect</span>
          </button>

          <div style={{ borderLeft: '1px solid #ccc', height: '20px', margin: '0 8px' }} />

          {/* Quick-access common components */}
          {[
            { name: 'Web Client',          label: 'Client'     },
            { name: 'Load Balancer',       label: 'LB'         },
            { name: 'API Gateway',         label: 'Gateway'    },
            { name: 'Application Server',  label: 'App'        },
            { name: 'Primary Database',    label: 'DB'         },
            { name: 'Cache (Redis)',        label: 'Cache'      },
            { name: 'Message Queue',       label: 'Queue'      },
            { name: 'CDN',                 label: 'CDN'        },
            { name: 'Cron Job',            label: 'Cron'       },
            { name: 'Producer/Consumer',   label: 'Pub/Sub'    },
          ].map(({ name, label }) => {
            const comp = HLD_COMPONENTS[name];
            if (!comp) return null;
            const IconComponent = comp.icon;
            const isActive = selectedComponent === name;
            return (
              <button
                key={name}
                title={name}
                onClick={() => {
                  setSelectedComponent(name);
                  setSelectedTool('component');
                  setConnectionStart(null);
                  setShowComponentDropdown(false);
                }}
                style={{
                  backgroundColor: isActive ? comp.color : 'white',
                  color: isActive ? 'white' : comp.color,
                  border: isActive ? `2px solid ${comp.color}` : `1px solid ${comp.color}`,
                  padding: '3px 6px',
                  borderRadius: 4,
                  height: '44px',
                  display: 'inline-flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  minWidth: '44px',
                  gap: '2px',
                }}
              >
                <IconComponent style={{ fontSize: '14px' }} />
                <span style={{ fontSize: '9px', fontWeight: 500, lineHeight: 1 }}>{label}</span>
              </button>
            );
          })}

          <div style={{ borderLeft: '1px solid #ccc', height: '20px', margin: '0 8px' }} />

          {/* Undo */}
          <button
            onClick={undo}
            disabled={historyVersion >= 0 && historyIndexRef.current <= 0}
            title="Undo (Ctrl+Z)"
            style={{
              backgroundColor: 'white',
              color: (historyVersion >= 0 && historyIndexRef.current > 0) ? '#333' : '#bbb',
              border: '1px solid #ccc',
              padding: '4px 8px',
              borderRadius: 4,
              height: '32px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: (historyVersion >= 0 && historyIndexRef.current > 0) ? 'pointer' : 'not-allowed',
            }}
          >
            <FaUndo style={{ fontSize: '12px' }} />
            <span style={{ fontSize: '11px', marginLeft: '4px' }}>Undo</span>
          </button>

          {/* Redo */}
          <button
            onClick={redo}
            disabled={historyVersion >= 0 && historyIndexRef.current >= historyRef.current.length - 1}
            title="Redo (Ctrl+Y)"
            style={{
              marginLeft: 4,
              backgroundColor: 'white',
              color: (historyVersion >= 0 && historyIndexRef.current < historyRef.current.length - 1) ? '#333' : '#bbb',
              border: '1px solid #ccc',
              padding: '4px 8px',
              borderRadius: 4,
              height: '32px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: (historyVersion >= 0 && historyIndexRef.current < historyRef.current.length - 1) ? 'pointer' : 'not-allowed',
            }}
          >
            <FaRedo style={{ fontSize: '12px' }} />
            <span style={{ fontSize: '11px', marginLeft: '4px' }}>Redo</span>
          </button>

          <div style={{ borderLeft: '1px solid #ccc', height: '20px', margin: '0 8px' }} />

          <button
            onClick={deleteSelected}
            disabled={!shapes.some((s) => s.selected) && !selectedConnection}
            style={{
              marginLeft: 4,
              backgroundColor: 'white',
              color: '#dc3545',
              border: '1px solid #ccc',
              padding: '4px 8px',
              borderRadius: 4,
              height: '32px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: (shapes.some((s) => s.selected) || selectedConnection) ? 1 : 0.5
            }}
            title="Delete Selected"
          >
            <FaTrash style={{ fontSize: '12px' }} />
            <span style={{ fontSize: '11px', marginLeft: '4px' }}>Delete</span>
          </button>

          <button
            onClick={clearBoard}
            style={{
              marginLeft: 4,
              backgroundColor: 'white',
              border: '1px solid #ccc',
              padding: '4px 8px',
              borderRadius: 4,
              height: '32px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Clear All"
          >
            Clear
          </button>

          <button
            onClick={handleSave}
            disabled={saveLoading}
            style={{
              marginLeft: 8,
              backgroundColor: hasUnsavedChanges ? '#ff9800' : '#008080',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              padding: '4px 16px',
              fontWeight: 500,
              height: '32px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: saveLoading ? 'not-allowed' : 'pointer',
              opacity: saveLoading ? 0.6 : 1
            }}
            title={hasUnsavedChanges ? 'Save changes to server' : 'All changes saved'}
          >
            {saveLoading ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={() => setDocumentVisible(!documentVisible)}
            style={{
              marginLeft: 8,
              backgroundColor: 'white',
              color: '#008080',
              border: '1px solid #008080',
              borderRadius: 4,
              padding: '4px 16px',
              fontWeight: 500,
              height: '32px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#e3f2fd';
              e.target.style.borderColor = '#006666';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.borderColor = '#008080';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
            {documentVisible ? 'Hide Doc' : 'View Doc'}
          </button>
        </div>



        {/* SVG Drawing Area */}
        <div ref={svgContainerRef} style={{
          flex: 1,
          margin: '5px',
          border: '2px solid #ddd',
          borderRadius: '8px',
          backgroundColor: '#1f2937',
          overflow: 'auto',
          position: 'relative'
        }}>
          <svg
            ref={svgRef}
            width={4000}
            height={3000}
            style={{
              display: 'block',
              cursor: selectedTool === "select" ? 'default' : 'crosshair',
              backgroundColor: '#1f2937'
            }}
            onClick={handleSvgClick}
            onDoubleClick={handleSvgDoubleClick}
            onMouseDown={handleSvgMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <defs>
              <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#374151" strokeWidth="0.5" />
              </pattern>
              <marker
                id="arrowhead"
                markerWidth="8"
                markerHeight="6"
                refX="8"
                refY="3"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <polygon points="0 0, 8 3, 0 6" fill="#ffffff" />
              </marker>
              <style>{`
                @keyframes blink {
                  0%, 100% { opacity: 1; }
                  50% { opacity: 0; }
                }
              `}</style>
            </defs>
            <rect width={4000} height={3000} fill="url(#grid)" />
            {connections.map(renderConnection)}
            {shapes.map(renderShape)}
            {shapes.map(renderResizeHandles)}
            
            {/* Selection Box */}
            {selectionBox && (
              <rect
                x={selectionBox.x}
                y={selectionBox.y}
                width={selectionBox.width}
                height={selectionBox.height}
                fill="rgba(59, 130, 246, 0.1)"
                stroke="#3b82f6"
                strokeWidth={1}
                strokeDasharray="4 4"
              />
            )}
            
            {/* Child Box Text Editing Overlay */}
            {renderChildBoxEditingOverlay()}

            {/* Table Cell Editing Overlay */}
            {editingCell && (() => {
              const table = shapes.find(s => s.id === editingCell.tableId && s.type === 'table');
              if (!table) return null;
              const HEADER_H = 32;
              const numRows = (table.rows || []).length;
              const ROW_H = numRows > 0
                ? Math.max(20, Math.round((table.size.height - HEADER_H) / numRows))
                : 30;
              const colW = table.size.width / (table.columns || []).length;
              const cellX = table.position.x + editingCell.colIndex * colW;
              const cellY = editingCell.rowIndex === -1
                ? table.position.y
                : table.position.y + HEADER_H + editingCell.rowIndex * ROW_H;
              const cellH = editingCell.rowIndex === -1 ? HEADER_H : ROW_H;
              return (
                <foreignObject key="cell-editor" x={cellX} y={cellY} width={colW} height={cellH} overflow="visible">
                  <div xmlns="http://www.w3.org/1999/xhtml">
                    <input
                      autoFocus
                      value={editingCellText}
                      onChange={(e) => setEditingCellText(e.target.value)}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); saveEditCell(); }
                        if (e.key === 'Escape') { e.preventDefault(); setEditingCell(null); setEditingCellText(''); }
                      }}
                      onBlur={saveEditCell}
                      style={{
                        width: '100%',
                        height: `${cellH}px`,
                        background: 'rgba(30,41,59,0.95)',
                        border: '2px solid #3b82f6',
                        color: editingCell.rowIndex === -1 ? '#fff' : '#e2e8f0',
                        fontWeight: editingCell.rowIndex === -1 ? 'bold' : 'normal',
                        fontSize: '12px',
                        fontFamily: 'Arial, sans-serif',
                        padding: '0 6px',
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </foreignObject>
              );
            })()}

            {/* Text Editing Overlay */}
            {editingShape && (() => {
              const shape = shapes.find(s => s.id === editingShape);
              if (!shape) return null;

              // For text annotations: rendered as a real HTML element outside the SVG (see below)
              if (shape.type === 'textAnnotation') {
                return null;
              }

              // For rectangles and ovals, position and size the overlay within the shape bounds
              if (shape.type === 'rectangle' || shape.type === 'oval') {
                // Use full width and height of shape, padding handled in inner div
                const textWidth = shape.size.width;
                // Use dynamic height based on textarea content, but at least the shape height
                const hPad = shape.type === 'oval' ? 30 : 16; // horizontal/vertical side padding
                const textHeight = Math.max(shape.size.height, editingTextareaHeight + hPad);
                // Center horizontally and vertically on editing center
                const textX = editingCenter.x - textWidth / 2;
                const textY = editingCenter.y - textHeight / 2;

                // Explicit vertical centering: push textarea down so it sits at the vertical midpoint
                const verticalPadTop = Math.max(0, Math.round((textHeight - editingTextareaHeight) / 2));

                // Use fixed font size for editing text
                const editingFontSize = FIXED_FONT_SIZE;
                
                return (
                  <foreignObject
                    x={textX}
                    y={textY}
                    width={textWidth}
                    height={textHeight}
                    overflow="visible"
                  >
                    <div
                      style={{
                        position: 'relative',
                        width: '100%',
                        height: `${textHeight}px`,
                        margin: '0',
                        paddingTop: `${verticalPadTop}px`,
                        paddingBottom: '0',
                        paddingLeft: shape.type === 'oval' ? '20px' : '8px',
                        paddingRight: shape.type === 'oval' ? '20px' : '8px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderRadius: '0',
                        boxSizing: 'border-box',
                        display: 'block',
                        overflow: shape.type === 'oval' ? 'hidden' : 'visible'
                      }}
                      onClick={(e) => e.stopPropagation()}
                      xmlns="http://www.w3.org/1999/xhtml"
                    >
                      <textarea
                        ref={textareaRef}
                        value={editingText}
                        onChange={(e) => {
                          let newText = e.target.value;
                          
                          // Remove zero-width space if user started typing real content
                          if (newText.length > 1 && newText.startsWith('\u200B') && newText[1] !== '\u200B') {
                            newText = newText.replace(/\u200B/g, ''); // Remove all zero-width spaces
                          } else if (newText === '\u200B') {
                            // Only zero-width space remains, keep it for cursor centering
                            // Don't change newText
                          }
                          
                          setEditingText(newText);
                          
                          // Auto-resize textarea to show all text
                          e.target.style.height = 'auto';
                          const scrollHeight = e.target.scrollHeight;
                          const minHeight = editingFontSize * 1.2;
                          const newHeight = Math.max(scrollHeight, minHeight);
                          e.target.style.height = newHeight + 'px';
                          setTextareaHeight(scrollHeight);
                          setEditingTextareaHeight(newHeight);
                          
                          // Dynamically resize shape as user types
                          resizeShapeForText(editingShape, newText);
                          
                          // Ensure text remains centered - reset scroll position for centered text
                          setTimeout(() => {
                            // For centered text, scrollLeft should be 0 (no horizontal scrolling needed)
                            // The textAlign: center handles the centering
                            e.target.scrollLeft = 0;
                            e.target.scrollTop = 0;
                          }, 0);
                        }}
                        onFocus={(e) => {
                          // Ensure all text is visible
                          e.target.style.height = 'auto';
                          const scrollHeight = e.target.scrollHeight;
                            const minHeight = editingFontSize * 1.2;
                          const newHeight = Math.max(scrollHeight, minHeight);
                          e.target.style.height = newHeight + 'px';
                          setEditingTextareaHeight(newHeight);
                            
                          // For empty textarea, insert invisible character to center cursor
                          if (!editingText || editingText.length === 0) {
                            // Use zero-width space to center cursor visually
                            setTimeout(() => {
                              e.target.value = '\u200B'; // Zero-width space
                              e.target.setSelectionRange(1, 1); // Place cursor after the space
                              setEditingText('\u200B');
                            }, 0);
                          } else {
                            // Place cursor at the end of text
                            setTimeout(() => {
                              e.target.setSelectionRange(editingText.length, editingText.length);
                              // Reset scroll position - textAlign: center handles centering
                              e.target.scrollLeft = 0;
                              e.target.scrollTop = 0;
                            }, 0);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                            e.preventDefault();
                            handleTextSave();
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            handleTextCancel();
                          }
                        }}
                        onBlur={handleTextSave}
                        autoFocus
                        spellCheck={false}
                        style={{
                          width: '100%',
                          minHeight: `${editingFontSize * 1.2}px`,
                          backgroundColor: 'transparent',
                          border: 'none',
                          outline: 'none',
                          color: '#ffffff',
                          fontSize: `${editingFontSize}px`,
                          padding: '0',
                          margin: '0',
                          caretColor: '#ffffff',
                          textAlign: 'center',
                          fontFamily: 'Arial, sans-serif',
                          lineHeight: `${editingFontSize * 1.2}px`,
                          fontWeight: 'normal',
                          fontStyle: 'normal',
                          letterSpacing: 'normal',
                          textRendering: 'auto',
                          WebkitFontSmoothing: 'auto',
                          MozOsxFontSmoothing: 'auto',
                          resize: 'none',
                          overflow: 'visible',
                          whiteSpace: 'pre-wrap',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          boxSizing: 'border-box',
                          textAlignLast: 'center',
                          display: 'block',
                          verticalAlign: 'middle',
                          textIndent: '0'
                        }}
                        wrap="soft"
                      />
                    </div>
                  </foreignObject>
                );
              }
              
              return null;
            })()}
          </svg>

          {/* Text annotation editor — rendered as real HTML so it never expands the SVG scroll area */}
          {editingShape && (() => {
            const shape = shapes.find(s => s.id === editingShape);
            if (!shape || shape.type !== 'textAnnotation') return null;
            const container = svgContainerRef.current;
            if (!container) return null;
            const scrollLeft = container.scrollLeft;
            const scrollTop  = container.scrollTop;
            const left = shape.position.x - scrollLeft;
            const top  = shape.position.y - scrollTop;
            return (
              <textarea
                key="annotation-editor-html"
                ref={textareaRef}
                value={editingText}
                onChange={(e) => {
                  const val = e.target.value;
                  setEditingText(val);
                  // Shrink first so scrollWidth/scrollHeight reflect content size
                  e.target.style.height = '1px';
                  e.target.style.width  = '1px';
                  const newH = Math.max(30, e.target.scrollHeight);
                  const newW = Math.max(60, e.target.scrollWidth);
                  e.target.style.height = newH + 'px';
                  e.target.style.width  = newW + 'px';
                  setEditingTextareaHeight(newH);
                  setEditingTextareaWidth(newW);
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault();
                    handleTextSave();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    handleTextCancel();
                  }
                }}
                onBlur={handleTextSave}
                autoFocus
                spellCheck={false}
                placeholder="Type"
                style={{
                  position: 'absolute',
                  left,
                  top,
                  width: `${editingTextareaWidth}px`,
                  minWidth: '60px',
                  minHeight: '30px',
                  height: `${editingTextareaHeight}px`,
                  background: 'transparent',
                  border: 'none',
                  color: '#e2e8f0',
                  fontSize: '14px',
                  fontFamily: 'Arial, sans-serif',
                  lineHeight: '1.45',
                  padding: '0',
                  resize: 'none',
                  outline: 'none',
                  whiteSpace: 'pre',
                  overflow: 'hidden',
                  wordBreak: 'normal',
                  overflowWrap: 'normal',
                  zIndex: 10,
                  pointerEvents: 'auto',
                }}
              />
            );
          })()}
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
